import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import LZString from "lz-string";
import { Toaster, toast } from "sonner";
import { motion, AnimatePresence, useAnimation } from "motion/react";
import { ProjectSetup, RateCard, AssetRateCard, Shot, Asset, BidVersion, ProjectDocument, AppSettings, TopSheet, FolderDefinition, PurchaseOrder, ShootingSchedule } from "./types";
import { ProjectSetupForm } from "./components/ProjectSetupForm";
import { ProjectInsights } from "./components/ProjectInsights";
import { RateCardForm } from "./components/RateCardForm";
import { ShotTable } from "./components/ShotTable";
import { AssetTable } from "./components/AssetTable";
import { Documents } from "./components/Documents";
import { VersionHistory } from "./components/VersionHistory";
import { ConfirmModal } from "./components/ConfirmModal";
import { ExportVersionModal } from "./components/ExportVersionModal";
import { BidSummaryDashboard } from "./components/BidSummaryDashboard";
import { ProjectDashboard } from "./components/ProjectDashboard";
import { PresenceAvatars } from "./components/PresenceAvatars";
import { ProjectSettings } from "./components/ProjectSettings";
import { Settings } from "./components/Settings";
import { SectionPillHeader } from "./components/SectionPillHeader";
import { CRM } from "./components/CRM";
import { People } from "./components/People";
import { Wiki } from "./components/Wiki";
import { ProjectGantt } from "./components/ProjectGantt";
import { VideoAnalysis } from "./components/VideoAnalysis";
import { Meetings } from "./components/Meetings";
import { ShotGridDashboard } from "./components/ShotGridDashboard";
import { FinanceDashboard } from "./components/FinanceDashboard";
import { ProjectUtilisation } from "./components/ProjectUtilisation";
import { ProjectShootCalendar } from "./components/ProjectShootCalendar";
import { ProjectShowDashboard } from "./components/ProjectShowDashboard";
import { ArtistBlockingCalendar } from "./components/ArtistBlockingCalendar";
import { Film, Home, LogOut, Save, ArrowLeft, CheckCircle2, Settings as SettingsIcon, Database, Tv, Settings2, Package, FileText, PieChart, LayoutGrid, Trash2, Info, Users, User, ChevronRight, Calendar, Loader2, Scan, Download, Sparkles, WalletCards, Layers3 } from "lucide-react";
import { useAuth } from "./contexts/AuthContext";
import { auth, db, firebaseApp } from "./firebase";
import { doc, setDoc, getDoc, deleteDoc, writeBatch, collection, getDocs } from "firebase/firestore/lite";
import { DEFAULT_RATES, DEFAULT_ASSET_RATES, DEFAULT_SCRIPT_PROMPTS } from "./constants";
import type { FptFinancialSummary, FptProfitabilitySummary, FptSyncStatus, Scene, Sequence } from "./models/production";
import { buildSceneRecordsFromScriptDocument, linkScenesToSequencesAndShots } from "./services/scriptSceneService";
import { buildEntitySyncMetadataMap } from "./services/fptSyncMetadata";
import { normalizeScriptSceneInput } from "./shared/sceneMappers";
import { buildPlaceholderShotCode, deriveHierarchyFromShotCode, inferShotCodeSettingsFromCodes } from "./shared/shotCodeParser";

import { cn, formatCurrency, getCompanyLogoUrl } from "./utils";
import { ExportFilterState, ExportScopeType, buildEpisodeBreakdown, calculateAssetValue, calculateShotValue, calculateSnapshotTotals, formatBidVersionDisplayName, getCurrentWorkingVersionCode, getNextVersionCode, isSubsetExportActive } from "./lib/bidVersioning";
import { isBidItemIncluded } from "./lib/bidStatus";

import { Sidebar } from "./components/Sidebar";
import { AIAssistant } from "./components/AIAssistant";
import Splash from "./pages/Splash";
import { InternalWelcome } from "./components/InternalWelcome";

import { AccessDenied } from "./components/AccessDenied";
import { generateProjectShowColour, getReadableTextColour, normalizeHexColour } from "./lib/projectShowColours";

export const DEFAULT_SETUP: ProjectSetup = {
  projectName: "Untitled Project",
  clientName: "",
  showCode: "",
  projectType: "episodic",
  supervisionPercentage: 10,
  onsetSupervision: 0,
  currency: "GBP",
  bidDate: new Date().toISOString().split("T")[0],
  status: "Bidding",
  person: "",
  assumptions: "- The project can be completed in the agreed timeframe.\n- Additional resources/time required to complete the project will be quoted and billed separately\n- If the brief changes and/or the required elements are not received by Vine FX to an agreed delivery schedule, further costs may be incurred",
  targetCurrency: "",
  exchangeRate: 1,
  shotCount: 5,
  projectStartEst: "",
  projectEndEst: "",
  distributorName: "",
};

const DISABLE_CLIENT_FIRESTORE_WATCHERS =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

const PROJECT_CACHE_VERSION = 2;

const buildDefaultProjectSetup = (defaultAssumptions?: string, colourSeed?: string): ProjectSetup => ({
  ...DEFAULT_SETUP,
  assumptions: defaultAssumptions || DEFAULT_SETUP.assumptions,
  themeAccentColor: generateProjectShowColour(colourSeed || `${Date.now()}-${Math.random()}`),
  themeAccentColorSource: "auto",
});

const ensureProjectSetupShowColour = (projectSetup: ProjectSetup, seed?: string): ProjectSetup => ({
  ...projectSetup,
  themeAccentColor: projectSetup.themeAccentColor || generateProjectShowColour(seed || projectSetup.projectName || projectSetup.showCode || "project"),
  themeAccentColorSource: projectSetup.themeAccentColorSource || "auto",
});

const getProjectCacheKey = (projectId: string) => `project_${projectId}`;

const stripDocumentForProjectCache = (doc: any) => {
  if (!doc || typeof doc !== "object") return doc;
  return {
    ...doc,
    content: undefined,
    analysisResult: undefined,
    extractedText: undefined,
    ocrText: undefined,
    textContent: undefined,
    parsedContent: undefined,
    pages: undefined,
  };
};

const stripShotForProjectCache = (shot: any) => {
  if (!shot || typeof shot !== "object") return shot;
  return {
    ...shot,
    thumbnail: null,
    thumbnailUrl: null,
    frameUrls: undefined,
  };
};

const stripAssetForProjectCache = (asset: any) => {
  if (!asset || typeof asset !== "object") return asset;
  return {
    ...asset,
    thumbnail: null,
    thumbnailUrl: null,
    previewUrls: undefined,
  };
};

const stripVersionForProjectCache = (version: any) => {
  if (!version || typeof version !== "object") return version;
  return {
    ...version,
    shots: Array.isArray(version.shots) ? version.shots.map(stripShotForProjectCache) : version.shots,
    assets: Array.isArray(version.assets) ? version.assets.map(stripAssetForProjectCache) : version.assets,
  };
};

const buildProjectCachePayload = (projectData: any) => ({
  ...projectData,
  __cacheVersion: PROJECT_CACHE_VERSION,
  documents: Array.isArray(projectData.documents) ? projectData.documents.map(stripDocumentForProjectCache) : projectData.documents,
  shots: Array.isArray(projectData.shots) ? projectData.shots.map(stripShotForProjectCache) : projectData.shots,
  assets: Array.isArray(projectData.assets) ? projectData.assets.map(stripAssetForProjectCache) : projectData.assets,
  versions: Array.isArray(projectData.versions) ? projectData.versions.map(stripVersionForProjectCache) : projectData.versions,
});

type ProjectFptMetadataState = {
  fptProjectId?: string | number | null;
  fptProjectName?: string;
  fptProjectUrl?: string;
  fptLinked?: boolean;
  lastPushedAt?: string;
  lastPulledAt?: string;
  lastSyncedBidVersion?: string;
  syncStatus?: FptSyncStatus;
  lastFptSyncAt?: string;
  fptSyncHash?: string;
  entitySyncMetadata?: Record<string, unknown>;
  identityMappings?: Record<string, unknown>;
  financials?: FptFinancialSummary;
  profitability?: FptProfitabilitySummary;
  fptStructureInspection?: unknown;
};

const pickNewerProjectProfitability = (
  current?: FptProfitabilitySummary,
  incoming?: FptProfitabilitySummary
): FptProfitabilitySummary | undefined => {
  if (!current) return incoming;
  if (!incoming) return current;

  const currentTime = current.lastComputedAt ? Date.parse(current.lastComputedAt) : Number.NaN;
  const incomingTime = incoming.lastComputedAt ? Date.parse(incoming.lastComputedAt) : Number.NaN;

  if (Number.isFinite(currentTime) && Number.isFinite(incomingTime)) {
    return incomingTime >= currentTime ? incoming : current;
  }

  if (Number.isFinite(incomingTime)) return incoming;
  if (Number.isFinite(currentTime)) return current;

  return incoming;
};

const pickNewerProjectFinancials = (
  current?: FptFinancialSummary,
  incoming?: FptFinancialSummary
): FptFinancialSummary | undefined => {
  if (!current) return incoming;
  if (!incoming) return current;

  const currentTime = current.lastTimelogPullAt ? Date.parse(current.lastTimelogPullAt) : Number.NaN;
  const incomingTime = incoming.lastTimelogPullAt ? Date.parse(incoming.lastTimelogPullAt) : Number.NaN;

  if (Number.isFinite(currentTime) && Number.isFinite(incomingTime)) {
    return incomingTime >= currentTime ? incoming : current;
  }

  if (Number.isFinite(incomingTime)) return incoming;
  if (Number.isFinite(currentTime)) return current;

  return incoming;
};

const rehydrateShots = (shots: Shot[]): Shot[] => {
  if (!Array.isArray(shots)) return [];
  return shots.map(shot => ({
    ...shot,
    days: {
      supervisor: shot.days?.supervisor || 0,
      concept: shot.days?.concept || 0,
      dmp: shot.days?.dmp || 0,
      comp: shot.days?.comp || 0,
      roto: shot.days?.roto || 0,
      prep: shot.days?.prep || 0,
      gfx: shot.days?.gfx || 0,
      cameraTR: shot.days?.cameraTR || 0,
      objectTR: shot.days?.objectTR || 0,
      rANIM: shot.days?.rANIM || 0,
      layout: shot.days?.layout || 0,
      anim: shot.days?.anim || 0,
      light: shot.days?.light || 0,
      fx: shot.days?.fx || 0,
    }
  }));
};

const rehydrateAssets = (assets: Asset[]): Asset[] => {
  if (!Array.isArray(assets)) return [];
  return assets.map(asset => ({
    ...asset,
    days: {
      supervisor: asset.days?.supervisor || 0,
      concept: asset.days?.concept || 0,
      dmp: asset.days?.dmp || 0,
      model: asset.days?.model || 0,
      rig: asset.days?.rig || 0,
      retop: asset.days?.retop || 0,
      cloth: asset.days?.cloth || 0,
      fur: asset.days?.fur || 0,
      groom: asset.days?.groom || 0,
      texture: asset.days?.texture || 0,
      anim: asset.days?.anim || 0,
      fx: asset.days?.fx || 0,
      lookdev: asset.days?.lookdev || 0,
      photoscan: asset.days?.photoscan || 0,
    }
  }));
};

const normalizeEpisodeLabel = (value?: string | null) => {
  if (!value) return "";
  return String(value).trim();
};

const getEpisodeNumberFromLabel = (value?: string | null) => {
  const normalized = normalizeEpisodeLabel(value);
  if (!normalized) return undefined;
  const match = normalized.match(/(\d+)$/);
  const raw = match ? match[1] : normalized;
  const parsed = Number.parseInt(raw, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const getScriptSceneInputsFromAnalysis = (analysisResult: any) => {
  const scriptData = analysisResult?.script_data || {};

  const explicitScenes = Array.isArray(scriptData.scenes) ? scriptData.scenes : [];
  if (explicitScenes.length > 0) {
    return explicitScenes
      .map((scene: any) =>
        normalizeScriptSceneInput({
          sceneNumber: scene.sceneNumber ?? scene.scene_number,
          heading: scene.heading,
          locationName: scene.locationName ?? scene.location_name ?? scene.location,
          intExt: scene.intExt ?? scene.int_ext,
          timeOfDay: scene.timeOfDay ?? scene.time_of_day,
          dayNight: scene.dayNight ?? scene.day_night,
          description: scene.description,
          scriptText: scene.scriptText ?? scene.script_text,
          characters: scene.characters,
          aiSummary: scene.aiSummary ?? scene.ai_summary,
          inferredVfxRequirements: scene.inferredVfxRequirements ?? scene.inferred_vfx_requirements,
          estimatedComplexity: scene.estimatedComplexity ?? scene.estimated_complexity,
          scriptPageRange: scene.scriptPageRange ?? scene.script_page_range ?? scene.page_range,
          sequenceNumber: scene.sequenceNumber ?? scene.sequence_number,
        })
      )
      .filter((scene: any) => scene.sceneNumber);
  }

  const sceneAnalysis = Array.isArray(scriptData.scene_analysis) ? scriptData.scene_analysis : [];
  if (sceneAnalysis.length > 0) {
    return sceneAnalysis
      .map((entry: unknown, index: number) => {
        const value = String(entry || "").trim();
        if (!value) return null;
        const matched = value.match(/^([^:]+):\s*(.*)$/);
        return normalizeScriptSceneInput({
          sceneNumber: matched?.[1]?.trim() || String(index + 1),
          description: matched?.[2]?.trim() || value,
          scriptText: matched?.[2]?.trim() || value,
        });
      })
      .filter(Boolean);
  }

  const sceneHeadings = Array.isArray(scriptData.scene_headings) ? scriptData.scene_headings : [];
  return sceneHeadings
    .map((entry: unknown, index: number) => {
      const value = String(entry || "").trim();
      if (!value) return null;
      return normalizeScriptSceneInput({
        sceneNumber: String(index + 1),
        heading: value,
        description: value,
      });
    })
    .filter(Boolean);
};

type AppView = "welcome" | "dashboard" | "project" | "settings" | "crm" | "finance" | "gantt" | "people" | "calendar" | "onset" | "wiki";
type ProjectTab = "projectdetails" | "show-dashboard" | "key" | "shots" | "assets" | "documents" | "exports" | "shotgrid" | "finance" | "utilisation" | "calendar" | "videoanalysis" | "meetings";

type AppRouteState = {
  view: AppView;
  projectId: string | null;
  activeTab: ProjectTab;
  isProjectSettingsView: boolean;
};

const DEFAULT_ROUTE_STATE: AppRouteState = {
  view: "welcome",
  projectId: null,
  activeTab: "show-dashboard",
  isProjectSettingsView: false,
};

const PROJECT_ROUTE_TABS = new Set<ProjectTab>([
  "projectdetails",
  "show-dashboard",
  "key",
  "shots",
  "assets",
  "documents",
  "exports",
  "shotgrid",
  "finance",
  "utilisation",
  "calendar",
  "videoanalysis",
  "meetings",
]);

const parseRoutePath = (pathname: string): AppRouteState => {
  const segments = pathname.split("/").filter(Boolean);

  if (segments.length === 0) {
    return DEFAULT_ROUTE_STATE;
  }

  if (segments[0] === "welcome") {
    return { ...DEFAULT_ROUTE_STATE, view: "welcome" };
  }

  if (segments[0] === "projects") {
    const projectId = segments[1] || null;
    if (!projectId) {
      return { ...DEFAULT_ROUTE_STATE, view: "dashboard" };
    }

    if (segments[2] === "settings") {
      return {
        view: "project",
        projectId,
        activeTab: "projectdetails",
        isProjectSettingsView: true,
      };
    }

    const maybeTab = segments[2] as ProjectTab | undefined;
    return {
      view: "project",
      projectId,
      activeTab: maybeTab && PROJECT_ROUTE_TABS.has(maybeTab) ? maybeTab : "show-dashboard",
      isProjectSettingsView: false,
    };
  }

  if (segments[0] === "relationships") {
    return { ...DEFAULT_ROUTE_STATE, view: "crm" };
  }

  if (segments[0] === "settings") {
    return { ...DEFAULT_ROUTE_STATE, view: "settings" };
  }

  if (segments[0] === "people") {
    return { ...DEFAULT_ROUTE_STATE, view: "people" };
  }

  if (segments[0] === "wiki") {
    return { ...DEFAULT_ROUTE_STATE, view: "wiki" };
  }

  if (segments[0] === "calendar") {
    return { ...DEFAULT_ROUTE_STATE, view: "calendar" };
  }

  if (segments[0] === "gantt") {
    return { ...DEFAULT_ROUTE_STATE, view: "gantt" };
  }

  if (segments[0] === "finance") {
    return { ...DEFAULT_ROUTE_STATE, view: "finance" };
  }

  if (segments[0] === "onset") {
    return { ...DEFAULT_ROUTE_STATE, view: "onset" };
  }

  return DEFAULT_ROUTE_STATE;
};

const buildRoutePath = ({ view, projectId, activeTab, isProjectSettingsView }: AppRouteState): string => {
  switch (view) {
    case "welcome":
      return "/welcome";
    case "dashboard":
      return "/projects";
    case "settings":
      return "/settings";
    case "crm":
      return "/relationships";
    case "wiki":
      return "/wiki";
    case "finance":
      return "/finance";
    case "people":
      return "/people";
    case "calendar":
      return "/calendar";
    case "gantt":
      return "/gantt";
    case "onset":
      return "/onset";
    case "project":
      if (!projectId) return "/";
      if (isProjectSettingsView) return `/projects/${projectId}/settings`;
      return activeTab === "show-dashboard"
        ? `/projects/${projectId}`
        : `/projects/${projectId}/${activeTab}`;
    default:
      return "/projects";
  }
};

// Helper to clean data for Firestore (remove undefined, replace with null or remove key)
const cleanDataForFirestore = (data: any): any => {
  if (data === undefined) return null;
  if (data === null) return null;
  if (Array.isArray(data)) return data.map(cleanDataForFirestore);
  if (typeof data === 'object' && data !== null) {
    const cleaned: any = {};
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        const val = data[key];
        if (val !== undefined) {
          cleaned[key] = cleanDataForFirestore(val);
        }
      }
    }
    return cleaned;
  }
  return data;
};

export default function App() {
  const { user, profile, effectiveProfile, login, logout, isAdmin, isStandard, hasPermission, loading, error } = useAuth();
  const [isStartingSubscription, setIsStartingSubscription] = useState(false);

  const [projectId, setProjectId] = useState<string | null>(null);

  const [setup, setSetup] = useState<ProjectSetup>(DEFAULT_SETUP);
  const [productionCompany, setProductionCompany] = useState<any>(null);
  const companyByIdCacheRef = useRef<Map<string, any>>(new Map());
  const companyByNameCacheRef = useRef<Map<string, any>>(new Map());
  const hasLoadedCompaniesLookupRef = useRef(false);
  const currentProjectAccentColor = normalizeHexColour(setup.themeAccentColor) || generateProjectShowColour(projectId || setup.projectName || setup.showCode || "project");
  const currentProjectAccentTextColor = getReadableTextColour(currentProjectAccentColor);

  const rememberCompany = useCallback((company: any) => {
    if (!company || typeof company !== "object") return;
    if (company.id != null) {
      companyByIdCacheRef.current.set(String(company.id), company);
    }
    if (company.name && typeof company.name === "string") {
      companyByNameCacheRef.current.set(company.name.trim().toLowerCase(), company);
    }
  }, []);

  const persistProjectCache = useCallback((targetProjectId: string, projectData: any) => {
    try {
      localStorage.setItem(getProjectCacheKey(targetProjectId), JSON.stringify(buildProjectCachePayload(projectData)));
      return true;
    } catch (storageError) {
      console.warn("Failed to cache lightweight project locally", storageError);
      return false;
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setProductionCompany(null);
      companyByIdCacheRef.current.clear();
      companyByNameCacheRef.current.clear();
      hasLoadedCompaniesLookupRef.current = false;
      return;
    }

    let cancelled = false;

    const loadProductionCompany = async () => {
      try {
        if (setup.productionCompanyId) {
          const cachedById = companyByIdCacheRef.current.get(String(setup.productionCompanyId));
          if (cachedById) {
            if (!cancelled) {
              setProductionCompany(cachedById);
            }
            return;
          }

          const res = await fetch(`/api/crm/companies/${setup.productionCompanyId}`, {
            headers: { "x-user-uid": user.uid }
          });
          const data = res.ok ? await res.json() : null;
          rememberCompany(data);
          if (!cancelled) {
            setProductionCompany(data);
          }
          return;
        }

        if (setup.clientName?.trim()) {
          const companyNameKey = setup.clientName.trim().toLowerCase();
          const cachedByName = companyByNameCacheRef.current.get(companyNameKey);
          if (cachedByName) {
            if (!cancelled) {
              setProductionCompany(cachedByName);
            }
            return;
          }

          if (!hasLoadedCompaniesLookupRef.current) {
            const res = await fetch("/api/crm/companies", {
              headers: { "x-user-uid": user.uid }
            });
            const companies = res.ok ? await res.json() : [];
            if (Array.isArray(companies)) {
              companies.forEach(rememberCompany);
            }
            hasLoadedCompaniesLookupRef.current = true;
          }

          const matchedCompany = companyByNameCacheRef.current.get(companyNameKey) || null;

          if (!cancelled) {
            setProductionCompany(matchedCompany || null);
          }
          return;
        }

        if (!cancelled) {
          setProductionCompany(null);
        }
      } catch (err) {
        console.error("Failed to fetch production company", err);
        if (!cancelled) {
          setProductionCompany(null);
        }
      }
    };

    loadProductionCompany();

    return () => {
      cancelled = true;
    };
  }, [rememberCompany, setup.productionCompanyId, setup.clientName, user]);

  const [rates, setRates] = useState<RateCard>(DEFAULT_RATES);

  const [assetRates, setAssetRates] = useState<AssetRateCard>(DEFAULT_ASSET_RATES);

  const [shots, setShots] = useState<Shot[]>([]);
  const [shotImportRevision, setShotImportRevision] = useState(0);
  const [isQuoteImporting, setIsQuoteImporting] = useState(false);

  const [assets, setAssets] = useState<Asset[]>([]);
  const [scenes, setScenes] = useState<Scene[]>([]);
  const [sequences, setSequences] = useState<Sequence[]>([]);
  const [shootingSchedules, setShootingSchedules] = useState<ShootingSchedule[]>([]);

  const [versions, setVersions] = useState<BidVersion[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [activeVersionId, setActiveVersionId] = useState<string | null>(null);
  const [shotExportFilter, setShotExportFilter] = useState<ExportFilterState | null>(null);
  const [assetExportFilter, setAssetExportFilter] = useState<ExportFilterState | null>(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const lastSavedDataRef = useRef<string>(JSON.stringify({ 
    setup: DEFAULT_SETUP, 
    rates: DEFAULT_RATES, 
    assetRates: DEFAULT_ASSET_RATES, 
    shots: [], 
    assets: [], 
    documents: [],
    scenes: [],
    sequences: [],
    shootingSchedules: [],
    purchaseOrders: []
  }));
  const lastRemoteSavedDataRef = useRef<string>(JSON.stringify({
    setup: DEFAULT_SETUP,
    rates: DEFAULT_RATES,
    assetRates: DEFAULT_ASSET_RATES,
    shots: [],
    assets: [],
    documents: [],
    scenes: [],
    sequences: [],
    shootingSchedules: [],
    purchaseOrders: []
  }));
  const lastSavedChunksRef = useRef<{ shots: string[], assets: string[] }>({ shots: [], assets: [] });
  const lastSavedDocumentsRef = useRef<Record<string, string>>({});
  const lastSavedProjectDataRef = useRef<string>("");
  const lastOpenedProjectIdRef = useRef<string | null>(null);
  const isNewProjectRef = useRef<boolean>(false);
  const isDeletingRef = useRef<boolean>(false);
  const savePromiseRef = useRef<Promise<void>>(Promise.resolve());
  const [versionToRevert, setVersionToRevert] = useState<BidVersion | null>(null);
  const [versionToDelete, setVersionToDelete] = useState<string | null>(null);
  const [storageError, setStorageError] = useState<boolean>(false);
  const [quotaError, setQuotaError] = useState<boolean>(false);
  const [isAIAssistantOpen, setIsAIAssistantOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<ProjectTab>("show-dashboard");
  const [currentView, setCurrentView] = useState<AppView>("welcome");
  const [peopleInitialSelectedPersonId, setPeopleInitialSelectedPersonId] = useState<string | null>(null);
  const [previousView, setPreviousView] = useState<AppView>("welcome");
  const lastProjectTabRef = useRef<ProjectTab>("show-dashboard");
  const routeSyncModeRef = useRef<"push" | "replace">("replace");
  const isApplyingRouteRef = useRef(false);
  const pendingRouteRef = useRef<Omit<AppRouteState, "view"> | null>(null);
  const [hasHydratedInitialRoute, setHasHydratedInitialRoute] = useState(false);

  const handleNavigate = (view: AppView, historyMode: "push" | "replace" = "push") => {
    if (currentView === "project" && view !== "project") {
      const currentState = currentStateRef.current;
      saveProjectState(
        projectId,
        currentState.setup,
        currentState.rates,
        currentState.assetRates,
        currentState.shots,
        currentState.assets,
        currentState.documents,
        currentState.purchaseOrders,
        user || undefined,
        true,
        currentState.versions,
        currentState.activeVersionId,
        true
      );
    }
    routeSyncModeRef.current = historyMode;
    setPreviousView(currentView);
    setCurrentView(view);
    setIsProjectSettingsView(false);
  };
  const [documents, setDocuments] = useState<ProjectDocument[]>([]);
  const [fptProjectId, setFptProjectId] = useState<string | null>(null);
  const projectFptMetadataRef = useRef<ProjectFptMetadataState>({});
  const [globalSettings, setGlobalSettings] = useState<AppSettings | null>(null);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [lastSavedSettings, setLastSavedSettings] = useState<string>("");
  const [isProjectLoading, setIsProjectLoading] = useState(false);
  const [isProjectSettingsView, setIsProjectSettingsView] = useState(false);
  const [isSettingUpDrive, setIsSettingUpDrive] = useState(false);
  const [driveConnected, setDriveConnected] = useState(false);
  const defaultProjectSetup = React.useMemo(
    () => buildDefaultProjectSetup(globalSettings?.defaultAssumptions, "default-project-template"),
    [globalSettings?.defaultAssumptions]
  );

  const syncRouteToBrowser = useCallback((route: AppRouteState) => {
    if (typeof window === "undefined" || isApplyingRouteRef.current) return;
    const nextPath = buildRoutePath(route);
    if (window.location.pathname === nextPath) return;
    const method = routeSyncModeRef.current === "push" ? "pushState" : "replaceState";
    window.history[method]({}, "", nextPath);
    routeSyncModeRef.current = "replace";
  }, []);

  const handleProjectTabChange = useCallback((tab: ProjectTab, historyMode: "push" | "replace" = "push") => {
    routeSyncModeRef.current = historyMode;
    setIsProjectSettingsView(false);
    lastProjectTabRef.current = tab;
    setActiveTab(tab);
  }, []);

  const openProjectSettings = useCallback((historyMode: "push" | "replace" = "push") => {
    routeSyncModeRef.current = historyMode;
    setIsProjectSettingsView(true);
  }, []);

  const closeProjectSettings = useCallback((historyMode: "push" | "replace" = "push") => {
    routeSyncModeRef.current = historyMode;
    setIsProjectSettingsView(false);
  }, []);

  useEffect(() => {
    if (!isProjectSettingsView && activeTab === "key") {
      setActiveTab("show-dashboard");
    }
  }, [activeTab, isProjectSettingsView]);

  useEffect(() => {
    if (!isProjectSettingsView) {
      lastProjectTabRef.current = activeTab;
    }
  }, [activeTab, isProjectSettingsView]);

  useEffect(() => {
    if (loading || !user || !hasHydratedInitialRoute) return;
    syncRouteToBrowser({
      view: currentView,
      projectId,
      activeTab,
      isProjectSettingsView,
    });
  }, [activeTab, currentView, hasHydratedInitialRoute, isProjectSettingsView, loading, projectId, syncRouteToBrowser, user]);

  // Ref to keep track of current state for sync listeners to avoid stale closures
  const currentStateRef = useRef({ setup, rates, assetRates, shots, assets, documents, scenes, sequences, shootingSchedules, versions, purchaseOrders, activeVersionId });
  useEffect(() => {
    if (quoteImportInProgressRef.current) {
      currentStateRef.current = {
        ...currentStateRef.current,
        setup,
        rates,
        assetRates,
        documents,
        scenes,
        sequences,
        shootingSchedules,
        versions,
        purchaseOrders,
        activeVersionId,
      };
      return;
    }

    currentStateRef.current = { setup, rates, assetRates, shots, assets, documents, scenes, sequences, shootingSchedules, versions, purchaseOrders, activeVersionId };
  }, [setup, rates, assetRates, shots, assets, documents, scenes, sequences, shootingSchedules, versions, purchaseOrders, activeVersionId]);

  const handleDocumentsSetupUpdate = useCallback((update: Partial<ProjectSetup> | ((prev: ProjectSetup) => Partial<ProjectSetup>)) => {
    setSetup(prev => {
      const partial = typeof update === "function" ? update(prev) : update;
      const next = { ...prev, ...partial };
      currentStateRef.current = { ...currentStateRef.current, setup: next };
      return next;
    });
  }, []);

  const handleSetupChange = useCallback((nextSetup: ProjectSetup | ((prev: ProjectSetup) => ProjectSetup)) => {
    setSetup(prev => {
      const resolved = typeof nextSetup === "function" ? nextSetup(prev) : nextSetup;
      currentStateRef.current = { ...currentStateRef.current, setup: resolved };
      return resolved;
    });
  }, []);

  const handleDocumentsRatesUpdate = useCallback((update: Partial<RateCard> | ((prev: RateCard) => Partial<RateCard>)) => {
    setRates(prev => {
      const partial = typeof update === "function" ? update(prev) : update;
      const next = { ...prev, ...partial };
      currentStateRef.current = { ...currentStateRef.current, rates: next };
      return next;
    });
  }, []);

  const handleDocumentsAssetRatesUpdate = useCallback((update: Partial<AssetRateCard> | ((prev: AssetRateCard) => Partial<AssetRateCard>)) => {
    setAssetRates(prev => {
      const partial = typeof update === "function" ? update(prev) : update;
      const next = { ...prev, ...partial };
      currentStateRef.current = { ...currentStateRef.current, assetRates: next };
      return next;
    });
  }, []);

  useEffect(() => {
    const fetchGlobalSettings = async (retries = 3) => {
      if (!user) return;
      try {
        const res = await fetch("/api/settings", {
          headers: { "x-user-uid": user.uid }
        });
        if (res.ok) {
          const data = await res.json();
          
          // Migrate legacy folder structure if needed
          let migratedFolders: FolderDefinition[] = [];
          if (Array.isArray(data.folderStructure)) {
            if (data.folderStructure && data.folderStructure.length > 0 && typeof data.folderStructure[0] === 'string') {
              migratedFolders = (data.folderStructure as string[]).map((name, i) => ({
                id: `legacy-${i}`,
                name,
                color: '#64748b',
                children: []
              }));
            } else {
              migratedFolders = data.folderStructure;
            }
          } else {
            migratedFolders = [
              { id: '1', name: "Bids", color: '#4986e7', children: [] },
              { id: '2', name: "Reference", color: '#9fe1e7', children: [] },
              { id: '3', name: "Assets", color: '#16a765', children: [] },
              { id: '4', name: "Legal", color: '#fad165', children: [] },
              { id: '5', name: "Production", color: '#f83a22', children: [] },
            ];
          }

          setGlobalSettings({
            defaultRates: data.defaultRates || DEFAULT_RATES,
            defaultAssetRates: data.defaultAssetRates || DEFAULT_ASSET_RATES,
            defaultAssumptions: data.defaultAssumptions || DEFAULT_SETUP.assumptions,
            scriptPrompts: Array.isArray(data.scriptPrompts) ? data.scriptPrompts : DEFAULT_SCRIPT_PROMPTS,
            inviteOnlyEnabled: data.inviteOnlyEnabled || false,
            domainRestrictionEnabled: data.domainRestrictionEnabled || false,
            allowedDomains: Array.isArray(data.allowedDomains) && data.allowedDomains.length > 0 ? data.allowedDomains : ["vinefx.com"],
            folderStructure: migratedFolders,
          });
        }
      } catch (error) {
        if (retries > 0) {
          setTimeout(() => fetchGlobalSettings(retries - 1), 2000);
        } else {
          console.error("Failed to fetch global settings:", error);
        }
      }
    };
    
    // Fetch once on mount or user change
    fetchGlobalSettings();
  }, [user]);

  // Debounced auto-save for global settings
  useEffect(() => {
    if (!user || !globalSettings) return;

    const saveSettings = async () => {
      setIsSavingSettings(true);
      try {
        const isAdmin = profile?.role === 'admin' || profile?.role === 'super_admin';
        const body = JSON.stringify(globalSettings);
        await fetch(`/api/settings${isAdmin ? '?global=true' : ''}`, {
          method: "POST",
          headers: { 
            "Content-Type": "application/json",
            "x-user-uid": user.uid
          },
          body
        });
        setLastSavedSettings(body);
      } catch (error) {
        console.error("Failed to auto-save global settings:", error);
      } finally {
        setIsSavingSettings(false);
      }
    };

    const currentBody = JSON.stringify(globalSettings);
    if (currentBody === lastSavedSettings) return;

    const timeoutId = setTimeout(saveSettings, 2000);
    return () => clearTimeout(timeoutId);
  }, [globalSettings, user, profile?.role]);

  const handleDisconnectDrive = async () => {
    if (!user) return;
    try {
      const res = await fetch("/api/drive/disconnect", {
        method: "POST",
        headers: { "x-user-uid": user.uid }
      });
      if (res.ok) {
        setDriveConnected(false);
        toast.success("Disconnected from Google Drive");
      }
    } catch (error) {
      console.error("Failed to disconnect from Google Drive", error);
      toast.error("Failed to disconnect from Google Drive");
    }
  };

  // CRM sync removed as per user request - domain users are synced from workspace
  /*
  useEffect(() => {
    const syncUserToCRM = async () => {
      if (!user || !profile) return;
      try {
        await fetch("/api/crm/people/sync", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-user-uid": user.uid
          },
          body: JSON.stringify({
            name: profile.displayName,
            email: profile.email,
            photoUrl: user.photoURL || ""
          })
        });
      } catch (error) {
        console.error("Failed to sync user to CRM", error);
      }
    };
    syncUserToCRM();
  }, [user, profile]);
  */

  useEffect(() => {
    const checkDriveStatus = async (retries = 3) => {
      if (!user) return;
      try {
        const res = await fetch("/api/drive/status", {
          headers: { "x-user-uid": user.uid }
        });
        if (!res.ok) throw new Error("Failed to fetch drive status");
        const { connected } = await res.json();
        setDriveConnected(connected);
      } catch (error) {
        if (retries > 0) {
          setTimeout(() => checkDriveStatus(retries - 1), 2000);
        } else {
          console.warn("Drive status check failed after retries. The server might be waking up.");
        }
      }
    };
    checkDriveStatus();

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'OAUTH_AUTH_SUCCESS') {
        checkDriveStatus();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [user]);

  const handleSetupDrive = async () => {
    if (!setup.projectName) {
      toast.error("Project must have a name to setup Google Drive");
      return;
    }
    setIsSettingUpDrive(true);
    try {
      const res = await fetch("/api/drive/setup-project", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "x-user-uid": user.uid
        },
        body: JSON.stringify({ 
          projectName: setup.projectName
        }),
      });
      const data = await res.json();
      if (data.success) {
        const formattedName = `project-${setup.projectName.replace(/\s+/g, '-').toLowerCase()}`;
        handleSetupChange(prev => ({
          ...prev,
          driveFolderId: data.folderIds.root,
          driveFolderName: formattedName,
          driveSubfolders: data.folderIds
        }));
        toast.success("Google Drive folders created successfully!");
      } else {
        toast.error(data.error || "Failed to setup Google Drive");
      }
    } catch (error) {
      console.error("Failed to setup Drive", error);
      toast.error("An error occurred while setting up Google Drive");
    } finally {
      setIsSettingUpDrive(false);
    }
  };

  const handleLinkDrive = async (driveId?: string, driveName?: string) => {
    if (!setup.projectName) {
      toast.error("Project must have a name to link Google Drive");
      return;
    }
    setIsSettingUpDrive(true);
    try {
      if (driveId) {
        // If a Shared Drive is selected, link it directly
        handleSetupChange(prev => ({
          ...prev,
          driveFolderId: driveId,
          driveFolderName: driveName || "Shared Drive",
          driveSubfolders: { root: driveId }
        }));
        toast.success(`Connected to selected Shared Drive`);
      } else {
        // If My Drive is selected, search for the project folder
        const res = await fetch(`/api/drive/find-project-folder?projectName=${encodeURIComponent(setup.projectName)}`, {
          headers: { "x-user-uid": user.uid }
        });
        const data = await res.json();
        if (data.success) {
          handleSetupChange(prev => ({
            ...prev,
            driveFolderId: data.folder.id,
            driveFolderName: data.folder.name,
            driveSubfolders: { root: data.folder.id }
          }));
          toast.success(`Connected to existing folder: ${data.folder.name}`);
        } else {
          toast.error(data.error || "Could not find a matching project folder on this drive");
        }
      }
    } catch (error) {
      console.error("Failed to link Drive", error);
      toast.error("An error occurred while linking Google Drive");
    } finally {
      setIsSettingUpDrive(false);
    }
  };
  const [isDeleteProjectConfirmOpen, setIsDeleteProjectConfirmOpen] = useState(false);
  const [isFirebaseConnected, setIsFirebaseConnected] = useState<boolean | null>(true);
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return saved === 'true';
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  useEffect(() => {
    console.log("Dark mode state changed:", darkMode);
    const root = window.document.documentElement;
    const body = window.document.body;
    if (darkMode) {
      root.classList.add('dark');
      body.classList.add('dark');
    } else {
      root.classList.remove('dark');
      body.classList.remove('dark');
    }
    localStorage.setItem('darkMode', darkMode.toString());
  }, [darkMode]);

  const toggleDarkMode = () => {
    console.log("Toggling dark mode from", darkMode, "to", !darkMode);
    setDarkMode(prev => !prev);
  };

  const handleStartSubscription = async () => {
    try {
      setIsStartingSubscription(true);
      await login();
    } finally {
      setIsStartingSubscription(false);
    }
  };

  const handleBookDemo = () => {
    window.location.href = "mailto:hello@vinefx.com?subject=Bidding%20Tools%20Demo%20Request";
  };

  useEffect(() => {
    if (!user) return;
    // Firebase is not used for data storage anymore, using local API
    setIsFirebaseConnected(true);
  }, [user]);


  const calculateTotalProjectCost = (
    currentShots: Shot[] = shots,
    currentAssets: Asset[] = assets,
    currentSetup = setup,
    currentRates = rates,
    currentAssetRates = assetRates
  ) => {
    if (!Array.isArray(currentShots) || !Array.isArray(currentAssets)) return 0;

    const snapshotTotals = calculateSnapshotTotals(
      currentShots,
      currentAssets,
      currentSetup,
      currentRates,
      currentAssetRates
    );
    if (snapshotTotals.grandTotal > 0) {
      return snapshotTotals.grandTotal;
    }

    const includedShots = currentShots.filter((shot) => shot && isBidItemIncluded(shot));
    const includedAssets = currentAssets.filter((asset) => asset && isBidItemIncluded(asset));
    const importedShotTotal = includedShots.reduce(
      (sum, shot) => sum + Number(shot.originalAwardedValue ?? shot.legacyPrice ?? 0),
      0
    );
    const importedAssetTotal = includedAssets.reduce(
      (sum, asset) => sum + Number(asset.originalAwardedValue ?? asset.legacyPrice ?? 0),
      0
    );
    const onsetSupervisionCost = (currentSetup.onsetSupervision || 0) * (currentRates.onsetSupervision || 0);

    return importedShotTotal + importedAssetTotal + onsetSupervisionCost;
  };

  const buildTopSheetDerivedData = (
    currentTopSheet: TopSheet | undefined,
    currentSetup = setup,
    currentShots: Shot[] = shots,
    currentAssets: Asset[] = assets,
    currentRates = rates,
    currentAssetRates = assetRates
  ) => {
    const activeShots = (currentShots || []).filter((shot) => shot && shot.active);
    const activeAssets = (currentAssets || []).filter((asset) => asset && asset.active);

    const shotTotal = activeShots.reduce((acc, shot) => {
      const baseTaskCost =
        (shot.days.concept || 0) * (currentRates.concept || 0) +
        (shot.days.dmp || 0) * (currentRates.dmp || 0) +
        (shot.days.comp || 0) * (currentRates.comp || 0) +
        (shot.days.roto || 0) * (currentRates.roto || 0) +
        (shot.days.prep || 0) * (currentRates.prep || 0) +
        (shot.days.gfx || 0) * (currentRates.gfx || 0) +
        (shot.days.cameraTR || 0) * (currentRates.cameraTR || 0) +
        (shot.days.objectTR || 0) * (currentRates.objectTR || 0) +
        (shot.days.rANIM || 0) * (currentRates.rANIM || 0) +
        (shot.days.layout || 0) * (currentRates.layout || 0) +
        (shot.days.anim || 0) * (currentRates.anim || 0) +
        (shot.days.light || 0) * (currentRates.light || 0) +
        (shot.days.fx || 0) * (currentRates.fx || 0);

      const taskDays =
        (shot.days.concept || 0) +
        (shot.days.dmp || 0) +
        (shot.days.comp || 0) +
        (shot.days.roto || 0) +
        (shot.days.prep || 0) +
        (shot.days.gfx || 0) +
        (shot.days.cameraTR || 0) +
        (shot.days.objectTR || 0) +
        (shot.days.rANIM || 0) +
        (shot.days.layout || 0) +
        (shot.days.anim || 0) +
        (shot.days.light || 0) +
        (shot.days.fx || 0);

      const supDays = taskDays * ((currentSetup.supervisionPercentage || 0) / 100);
      const supCost = supDays * (currentRates.supervisor || 0);
      return acc + (baseTaskCost + supCost) * (shot.quantity || 1);
    }, 0);

    const assetTotal = activeAssets.reduce((acc, asset) => {
      const baseTaskCost =
        (asset.days.concept || 0) * (currentAssetRates.concept || 0) +
        (asset.days.dmp || 0) * (currentAssetRates.dmp || 0) +
        (asset.days.model || 0) * (currentAssetRates.model || 0) +
        (asset.days.rig || 0) * (currentAssetRates.rig || 0) +
        (asset.days.retop || 0) * (currentAssetRates.retop || 0) +
        (asset.days.cloth || 0) * (currentAssetRates.cloth || 0) +
        (asset.days.fur || 0) * (currentAssetRates.fur || 0) +
        (asset.days.groom || 0) * (currentAssetRates.groom || 0) +
        (asset.days.texture || 0) * (currentAssetRates.texture || 0) +
        (asset.days.anim || 0) * (currentAssetRates.anim || 0) +
        (asset.days.fx || 0) * (currentAssetRates.fx || 0) +
        (asset.days.lookdev || 0) * (currentAssetRates.lookdev || 0) +
        (asset.days.photoscan || 0) * (currentAssetRates.photoscan || 0);

      const taskDays =
        (asset.days.concept || 0) +
        (asset.days.dmp || 0) +
        (asset.days.model || 0) +
        (asset.days.rig || 0) +
        (asset.days.retop || 0) +
        (asset.days.cloth || 0) +
        (asset.days.fur || 0) +
        (asset.days.groom || 0) +
        (asset.days.texture || 0) +
        (asset.days.anim || 0) +
        (asset.days.fx || 0) +
        (asset.days.lookdev || 0) +
        (asset.days.photoscan || 0);

      const supDays = taskDays * ((currentSetup.supervisionPercentage || 0) / 100);
      const supCost = supDays * (currentAssetRates.supervisor || 0);
      return acc + (baseTaskCost + supCost) * (asset.quantity || 1);
    }, 0);

    const onsetTotal = (currentSetup.onsetSupervision || 0) * (currentRates.onsetSupervision || 0);
    const grandTotal = shotTotal + assetTotal + onsetTotal;
    const importedGrandTotal = currentTopSheet?.importedReference?.costs?.grandTotal;
    const varianceAmount =
      importedGrandTotal !== undefined && importedGrandTotal !== null
        ? grandTotal - importedGrandTotal
        : undefined;
    const hasVariance =
      varianceAmount !== undefined ? Math.abs(varianceAmount) > 0.01 : false;

    return {
      calculated: {
        shotTotal,
        assetTotal,
        onsetTotal,
        grandTotal,
        shotCount: activeShots.length,
        assetCount: activeAssets.length,
        currency: currentSetup.currency || "GBP",
      },
      comparison: {
        importedGrandTotal,
        calculatedGrandTotal: grandTotal,
        varianceAmount,
        hasVariance,
        warning: hasVariance ? "Imported top sheet total differs from calculated total." : undefined,
      }
    };
  };

  const stableTotalProjectCostRef = useRef(0);
  const totalProjectCost = useMemo(() => {
    if (isQuoteImporting) {
      return stableTotalProjectCostRef.current;
    }
    const nextTotal = calculateTotalProjectCost(shots, assets, setup, rates, assetRates);
    stableTotalProjectCostRef.current = nextTotal;
    return nextTotal;
  }, [shots, assets, setup, rates, assetRates, isQuoteImporting]);

  const saveDocument = async (docToSave: ProjectDocument) => {
    if (!user || !projectId) return;
    try {
      const res = await fetch(`/api/projects/${projectId}/documents/${docToSave.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid
        },
        body: JSON.stringify(docToSave)
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || "Failed to save document");
      }

      void (async () => {
        try {
          const safeDoc = JSON.parse(JSON.stringify(docToSave, (k, v) => v === undefined ? null : v));
          await setDoc(doc(db, 'projects', projectId, 'documents', safeDoc.id), safeDoc);
        } catch (e) {
          console.error("Failed to save document to Firestore", e);
        }
      })();

      const latestDocuments = Array.isArray(currentStateRef.current.documents) ? currentStateRef.current.documents : [];
      const updatedDocuments = latestDocuments.some(d => d.id === docToSave.id)
        ? latestDocuments.map(d => d.id === docToSave.id ? docToSave : d)
        : [docToSave, ...latestDocuments];

      setDocuments(updatedDocuments);
      currentStateRef.current = { ...currentStateRef.current, documents: updatedDocuments };
    } catch (error) {
      console.error("Error saving document:", error);
      toast.error(error instanceof Error ? `Failed to save document metadata: ${error.message}` : "Failed to save document metadata");
    }
  };

  const persistScriptStructureFromDocument = useCallback(async (docToPersist: ProjectDocument) => {
    if (!projectId || !docToPersist.analysisResult) return;

    const parsedScenes = getScriptSceneInputsFromAnalysis(docToPersist.analysisResult);
    if (!parsedScenes.length) return;

    const currentShots = Array.isArray(currentStateRef.current.shots) ? currentStateRef.current.shots : [];
    const episodeNumber = getEpisodeNumberFromLabel(docToPersist.episode);
    const matchingShots = currentShots.filter((shot) => {
      const hierarchy = deriveHierarchyFromShotCode(shot.shotCode);
      return episodeNumber !== undefined && hierarchy.episodeNumber === episodeNumber;
    });

    const hierarchySamples = matchingShots
      .map((shot) => deriveHierarchyFromShotCode(shot.shotCode))
      .filter((item) => item.episodeNumber === episodeNumber);

    const episodeCode = hierarchySamples[0]?.seasonEpisodeCode
      || (episodeNumber !== undefined ? String(episodeNumber).padStart(2, "0") : normalizeEpisodeLabel(docToPersist.episode));
    const seasonNumber = hierarchySamples[0]?.seasonNumber;
    const seasonId = seasonNumber !== undefined ? `${projectId}:season:${seasonNumber}` : undefined;
    const episodeId = `${projectId}:episode:${episodeCode}`;

    const existingScenes = (currentStateRef.current.scenes || []).filter((scene) => scene.episodeId === episodeId);
    const sceneResult = buildSceneRecordsFromScriptDocument({
      projectId,
      episodeId,
      seasonId,
      parsedScenes,
      existingScenes,
    });

    const linkResult = linkScenesToSequencesAndShots({
      projectId,
      scenes: sceneResult.scenes,
      shots: matchingShots,
    });

    const nextScenesMap = new Map((currentStateRef.current.scenes || []).map((scene) => [scene.id, scene]));
    for (const scene of sceneResult.scenes) {
      nextScenesMap.set(scene.id, scene);
    }
    for (const { sceneId, patch } of linkResult.scenePatches) {
      const existing = nextScenesMap.get(sceneId);
      if (existing) {
        nextScenesMap.set(sceneId, { ...existing, ...patch, updatedAt: new Date().toISOString() });
      }
    }
    const nextScenes = Array.from(nextScenesMap.values());

    const nextShots = currentShots.map((shot) => {
      const patch = linkResult.shotPatches.find((entry) => entry.shotId === shot.id)?.patch;
      if (!patch) return shot;
      const linkedScene = nextScenes.find((scene) => scene.id === (patch.sceneId || shot.sceneId));
      const sceneDescription = linkedScene
        ? [linkedScene.locationName, linkedScene.intExt, linkedScene.dayNight, linkedScene.description].filter(Boolean).join(" - ")
        : shot.sceneDescription;
      return {
        ...shot,
        ...patch,
        sceneDescription: sceneDescription || shot.sceneDescription,
      };
    });

    const sequenceIds = new Set(
      nextScenes
        .filter((scene) => scene.episodeId === episodeId && scene.sequenceId)
        .map((scene) => scene.sequenceId as string)
    );

    const existingSequencesMap = new Map((currentStateRef.current.sequences || []).map((sequence) => [sequence.id, sequence]));
    for (const sequenceId of sequenceIds) {
      const sequenceScenes = nextScenes.filter((scene) => scene.sequenceId === sequenceId);
      const firstScene = sequenceScenes[0];
      if (!firstScene?.sequenceNumber) continue;
      const sequenceShots = nextShots.filter((shot) => shot.sequenceId === sequenceId);
      const existingSequence = existingSequencesMap.get(sequenceId);
      const sequenceCode = setup.showCode
        ? [
            setup.showCode,
            ...(setup.projectType === "feature_film" || setup.projectType === "other" ? [] : [episodeCode]),
            String(firstScene.sequenceNumber).padStart(3, "0"),
          ].join("_")
        : [
            ...(setup.projectType === "feature_film" || setup.projectType === "other" ? [] : [episodeCode]),
            String(firstScene.sequenceNumber).padStart(3, "0"),
          ].join("_");
      existingSequencesMap.set(sequenceId, {
        id: sequenceId,
        projectId,
        seasonId: seasonId || existingSequence?.seasonId,
        episodeId,
        sequenceNumber: firstScene.sequenceNumber,
        code: sequenceCode,
        title: existingSequence?.title,
        derivedFromSceneNumber: firstScene.sceneNumber,
        sceneIds: sequenceScenes.map((scene) => scene.id),
        shotCount: sequenceShots.length,
        taskCount: existingSequence?.taskCount,
        versionCount: existingSequence?.versionCount,
        progressStatusSummary: existingSequence?.progressStatusSummary,
        createdAt: existingSequence?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    }
    const nextSequences = Array.from(existingSequencesMap.values());

    setScenes(nextScenes);
    setSequences(nextSequences);
    setShots(nextShots);
    currentStateRef.current = {
      ...currentStateRef.current,
      scenes: nextScenes,
      sequences: nextSequences,
      shots: nextShots,
    };
  }, [projectId, setup.projectType, setup.showCode]);

  const removeDocument = async (docId: string) => {
    if (!projectId) return;
    const latestDocuments = Array.isArray(currentStateRef.current.documents) ? currentStateRef.current.documents : [];
    const removedDocument = latestDocuments.find(d => d.id === docId);
    const updatedDocuments = latestDocuments.filter(d => d.id !== docId);

    setDocuments(updatedDocuments);
    currentStateRef.current = { ...currentStateRef.current, documents: updatedDocuments };

    if (!user) {
      toast.error("Please wait for the project session to finish loading before deleting documents.");
      if (removedDocument) {
        setDocuments(latestDocuments);
        currentStateRef.current = { ...currentStateRef.current, documents: latestDocuments };
      }
      return;
    }

    try {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 8000);
      const res = await fetch(`/api/projects/${projectId}/documents/${docId}`, {
        method: "DELETE",
        headers: {
          "x-user-uid": user.uid
        },
        signal: controller.signal
      });
      window.clearTimeout(timeout);
      if (!res.ok) throw new Error("Failed to delete document");

      void (async () => {
        try {
          await deleteDoc(doc(db, 'projects', projectId, 'documents', docId));
        } catch (e) {
          console.error("Failed to delete document from Firestore", e);
        }
      })();
    } catch (error) {
      console.error("Error deleting document:", error);
      if (removedDocument) {
        setDocuments(latestDocuments);
        currentStateRef.current = { ...currentStateRef.current, documents: latestDocuments };
      }
      toast.error("Failed to delete document");
    }
  };

  const saveProjectState = async (
    currentProjectId = projectId,
    currentSetup = setup,
    currentRates = rates,
    currentAssetRates = assetRates,
    currentShots = shots,
    currentAssets = assets,
    currentDocuments = documents,
    currentPurchaseOrders = purchaseOrders,
    currentUser = user,
    isExplicit = false,
    overrideVersions?: BidVersion[],
    overrideActiveVersionId?: string | null,
    syncRemote = true
  ) => {
    if (!currentUser || !currentProjectId || isProjectLoading || isDeletingRef.current) return;
    
    // 1. Immediate Local Save
    const shotsArr = Array.isArray(currentShots) ? currentShots : [];
    const assetsArr = Array.isArray(currentAssets) ? currentAssets : [];
    const entitySyncMetadata = buildEntitySyncMetadataMap(
      shotsArr,
      assetsArr,
      projectFptMetadataRef.current.lastSyncedBidVersion,
      projectFptMetadataRef.current.entitySyncMetadata as Record<string, any> | undefined
    );
    const shotsWithSyncMetadata = shotsArr.map((shot) => ({
      ...shot,
      syncMetadata: entitySyncMetadata[shot.id],
    }));
    const assetsWithSyncMetadata = assetsArr.map((asset) => ({
      ...asset,
      syncMetadata: entitySyncMetadata[asset.id],
    }));
    projectFptMetadataRef.current = {
      ...projectFptMetadataRef.current,
      entitySyncMetadata,
    };
    const totalCost = calculateTotalProjectCost(shotsArr, assetsArr, currentSetup, currentRates, currentAssetRates);
    
    let currentVersions = Array.isArray(overrideVersions !== undefined ? overrideVersions : versions) 
      ? (overrideVersions !== undefined ? overrideVersions : versions) 
      : [];
    let currentActiveVersionId = overrideActiveVersionId !== undefined ? overrideActiveVersionId : activeVersionId;
    const latestVersion = currentVersions.length > 0
      ? [...currentVersions].sort((a, b) => new Date(b.exportedAt || b.createdAt).getTime() - new Date(a.exportedAt || a.createdAt).getTime())[0]
      : null;

    const projectData = {
      id: currentProjectId,
      projectName: (currentSetup.projectName || "Untitled Project").substring(0, 99),
      clientName: currentSetup.clientName,
      showCode: currentSetup.showCode,
      bidDate: currentSetup.bidDate,
      currency: currentSetup.currency || "GBP",
      status: currentSetup.status || "Bidding",
      projectStartEst: currentSetup.projectStartEst,
      projectEndEst: currentSetup.projectEndEst,
      setup: currentSetup,
      rates: currentRates,
      assetRates: currentAssetRates,
      latestTotalCost: totalCost,
      latestVersionName: latestVersion?.versionName || latestVersion?.versionCode,
      shots: shotsWithSyncMetadata,
      assets: assetsWithSyncMetadata,
      documents: currentDocuments,
      scenes: currentStateRef.current.scenes,
      sequences: currentStateRef.current.sequences,
      shootingSchedules: currentStateRef.current.shootingSchedules,
      purchaseOrders: currentPurchaseOrders,
      versions: currentVersions,
      activeVersionId: currentActiveVersionId,
      ownerId: currentUser.uid,
      updatedAt: new Date().toISOString(),
      ...projectFptMetadataRef.current,
      entitySyncMetadata,
    };

    persistProjectCache(currentProjectId, projectData);
    setLastSaved(new Date());

    const draftSnapshot = JSON.stringify({
      setup: currentSetup,
      rates: currentRates,
      assetRates: currentAssetRates,
      shots: currentShots,
      assets: currentAssets,
      documents: currentDocuments,
      scenes: currentStateRef.current.scenes,
      sequences: currentStateRef.current.sequences,
      shootingSchedules: currentStateRef.current.shootingSchedules,
      purchaseOrders: currentPurchaseOrders
    });
    lastSavedDataRef.current = draftSnapshot;

    if (!syncRemote) {
      try {
        await fetch(`/api/projects/${currentProjectId}?localOnly=1`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-user-uid": currentUser.uid
          },
          body: JSON.stringify(projectData)
        });
      } catch (error) {
        console.error("Local project file sync failed:", error);
      }
      return;
    }

    // 2. Background Sync to Server and Firestore
    try {
      setIsSyncing(true);
      
      // Direct Firestore sync
      try {
        const cleanedProjectData = cleanDataForFirestore(projectData);
        const { documents: projectDocs, versions: projectVersions, shots: projectShots, assets: projectAssets, scenes: projectScenes, sequences: projectSequences, ...mainProjectData } = cleanedProjectData;
        const safeMainProjectData = JSON.parse(JSON.stringify(mainProjectData, (k, val) => val === undefined ? null : val));
        await setDoc(doc(db, 'projects', currentProjectId), safeMainProjectData);
        
        // Save documents to subcollection
        if (projectDocs && projectDocs.length > 0) {
          await Promise.all(projectDocs.map(d => {
            // Extra safety check to ensure absolutely no undefined values
            const safeDoc = JSON.parse(JSON.stringify(d, (k, v) => v === undefined ? null : v));
            return setDoc(doc(db, 'projects', currentProjectId, 'documents', safeDoc.id), safeDoc);
          }));
        }
        
        // Save versions to subcollection
        if (projectVersions && projectVersions.length > 0) {
          await Promise.all(projectVersions.map(v => {
            const safeVersion = JSON.parse(JSON.stringify(v, (k, val) => val === undefined ? null : val));
            // Preserve thumbnails in versions if possible (aware of 1MB limit)
            if (safeVersion.shots) {
              safeVersion.shots = safeVersion.shots.map((s: any) => ({ ...s, thumbnail: s.thumbnail || null }));
            }
            if (safeVersion.assets) {
              safeVersion.assets = safeVersion.assets.map((a: any) => ({ ...a, thumbnail: a.thumbnail || null }));
            }
            return setDoc(doc(db, 'projects', currentProjectId, 'versions', safeVersion.id), safeVersion);
          }));
        }

        // Save shots to subcollection
        if (projectShots && projectShots.length > 0) {
          await Promise.all(projectShots.map(s => {
            const safeShot = JSON.parse(JSON.stringify(s, (k, val) => val === undefined ? null : val));
            return setDoc(doc(db, 'projects', currentProjectId, 'shots', safeShot.id), safeShot);
          }));
        }

        // Save assets to subcollection
        if (projectAssets && projectAssets.length > 0) {
          await Promise.all(projectAssets.map(a => {
            const safeAsset = JSON.parse(JSON.stringify(a, (k, val) => val === undefined ? null : val));
            return setDoc(doc(db, 'projects', currentProjectId, 'assets', safeAsset.id), safeAsset);
          }));
        }

        if (projectScenes && projectScenes.length > 0) {
          await Promise.all(projectScenes.map((scene: any) => {
            const safeScene = JSON.parse(JSON.stringify(scene, (k, val) => val === undefined ? null : val));
            return setDoc(doc(db, 'projects', currentProjectId, 'scenes', safeScene.id), safeScene);
          }));
        }

        if (projectSequences && projectSequences.length > 0) {
          await Promise.all(projectSequences.map((sequence: any) => {
            const safeSequence = JSON.parse(JSON.stringify(sequence, (k, val) => val === undefined ? null : val));
            return setDoc(doc(db, 'projects', currentProjectId, 'sequences', safeSequence.id), safeSequence);
          }));
        }
      } catch (fsError) {
        console.error("Direct Firestore sync failed:", fsError);
      }

      const res = await fetch(`/api/projects/${currentProjectId}`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          "x-user-uid": currentUser.uid
        },
        body: JSON.stringify(projectData)
      });

      if (!res.ok && res.status === 404) {
        await fetch("/api/projects", {
          method: "POST",
          headers: { 
            "Content-Type": "application/json",
            "x-user-uid": currentUser.uid
          },
          body: JSON.stringify(projectData)
        });
      }

      setLastSyncedAt(new Date());
      lastRemoteSavedDataRef.current = draftSnapshot;
    } catch (error) {
      console.error("Background sync failed:", error);
      // We don't toast error here because it's a background sync and we have local data
    } finally {
      setIsSyncing(false);
    }
  };

  const handleFptLinkStateChange = useCallback(
    (metadata: {
      fptProjectId: string;
      fptProjectName?: string | null;
      fptProjectUrl?: string | null;
      fptLinked: boolean;
      syncStatus?: FptSyncStatus | null;
      lastFptSyncAt?: string | null;
    }) => {
      projectFptMetadataRef.current = {
        ...projectFptMetadataRef.current,
        fptProjectId: metadata.fptProjectId,
        fptProjectName: metadata.fptProjectName || undefined,
        fptProjectUrl: metadata.fptProjectUrl || undefined,
        fptLinked: metadata.fptLinked,
        syncStatus: metadata.syncStatus || projectFptMetadataRef.current.syncStatus,
        lastFptSyncAt: metadata.lastFptSyncAt || projectFptMetadataRef.current.lastFptSyncAt,
      };
      setFptProjectId(metadata.fptProjectId ? String(metadata.fptProjectId) : null);
      void saveProjectState(
        projectId,
        currentStateRef.current.setup,
        currentStateRef.current.rates,
        currentStateRef.current.assetRates,
        currentStateRef.current.shots,
        currentStateRef.current.assets,
        currentStateRef.current.documents,
        currentStateRef.current.purchaseOrders,
        user || undefined,
        false,
        currentStateRef.current.versions,
        currentStateRef.current.activeVersionId,
        false
      );
    },
    [projectId, user]
  );

  const handleShotGridMetadataRefresh = useCallback(
    (metadata: {
      fptProjectId?: string | null;
      fptProjectName?: string | null;
      fptProjectUrl?: string | null;
      fptLinked?: boolean;
      lastPulledAt?: string;
      financials?: FptFinancialSummary;
      profitability?: FptProfitabilitySummary;
      fptStructureInspection?: unknown;
    }) => {
      projectFptMetadataRef.current = {
        ...projectFptMetadataRef.current,
        fptProjectId: metadata.fptProjectId ?? projectFptMetadataRef.current.fptProjectId,
        fptProjectName: metadata.fptProjectName || projectFptMetadataRef.current.fptProjectName,
        fptProjectUrl: metadata.fptProjectUrl || projectFptMetadataRef.current.fptProjectUrl,
        fptLinked: metadata.fptLinked ?? projectFptMetadataRef.current.fptLinked,
        lastPulledAt: metadata.lastPulledAt || projectFptMetadataRef.current.lastPulledAt,
        financials: pickNewerProjectFinancials(projectFptMetadataRef.current.financials, metadata.financials),
        profitability: pickNewerProjectProfitability(projectFptMetadataRef.current.profitability, metadata.profitability),
        fptStructureInspection: metadata.fptStructureInspection || projectFptMetadataRef.current.fptStructureInspection,
      };
      if (metadata.fptProjectId) {
        setFptProjectId(String(metadata.fptProjectId));
      }
    },
    []
  );

  useEffect(() => {
    if (!user || !projectId || isProjectLoading || isDeletingRef.current || currentView === "dashboard") return;
    if (quoteImportInProgressRef.current) return;
    
    // Check if data actually changed since last save
    const currentData = JSON.stringify({ setup, rates, assetRates, shots, assets, documents, scenes, sequences, shootingSchedules, purchaseOrders });
    if (currentData === lastSavedDataRef.current) return;

    const timeoutId = setTimeout(() => {
      saveProjectState(projectId, setup, rates, assetRates, shots, assets, documents, purchaseOrders, user, false, undefined, undefined, false);
    }, 3000); // Fast local-only draft save after changes settle
    return () => clearTimeout(timeoutId);
  }, [user, projectId, setup, rates, assetRates, shots, assets, documents, scenes, sequences, shootingSchedules, purchaseOrders, isProjectLoading, currentView]);

  useEffect(() => {
    if (!user || !projectId || isProjectLoading || isDeletingRef.current || currentView !== "project") return;
    if (quoteImportInProgressRef.current) return;

    const currentData = JSON.stringify({ setup, rates, assetRates, shots, assets, documents, scenes, sequences, shootingSchedules, purchaseOrders });
    if (currentData === lastRemoteSavedDataRef.current) return;

    const timeoutId = setTimeout(() => {
      saveProjectState(projectId, setup, rates, assetRates, shots, assets, documents, purchaseOrders, user, false, undefined, undefined, true);
    }, 45000); // Slower remote sync to reduce Firestore churn

    return () => clearTimeout(timeoutId);
  }, [user, projectId, setup, rates, assetRates, shots, assets, documents, scenes, sequences, shootingSchedules, purchaseOrders, isProjectLoading, currentView]);

  useEffect(() => {
    if (!user || !projectId || currentView !== "project") return;

    const syncDraftWhenHidden = () => {
      if (quoteImportInProgressRef.current) return;
      if (document.visibilityState === "hidden") {
        saveProjectState(projectId, setup, rates, assetRates, shots, assets, documents, purchaseOrders, user, true, undefined, undefined, true);
      }
    };

    const persistDraftOnUnload = () => {
      if (quoteImportInProgressRef.current) return;
      saveProjectState(projectId, setup, rates, assetRates, shots, assets, documents, purchaseOrders, user, true, undefined, undefined, false);
    };

    document.addEventListener("visibilitychange", syncDraftWhenHidden);
    window.addEventListener("pagehide", persistDraftOnUnload);
    window.addEventListener("beforeunload", persistDraftOnUnload);

    return () => {
      document.removeEventListener("visibilitychange", syncDraftWhenHidden);
      window.removeEventListener("pagehide", persistDraftOnUnload);
      window.removeEventListener("beforeunload", persistDraftOnUnload);
    };
  }, [user, projectId, setup, rates, assetRates, shots, assets, documents, scenes, sequences, shootingSchedules, purchaseOrders, currentView]);

  const [openingProjectId, setOpeningProjectId] = useState<string | null>(null);
  const quoteImportInProgressRef = useRef(false);

  const handleQuoteImportStateChange = useCallback((isImporting: boolean) => {
    quoteImportInProgressRef.current = isImporting;
    setIsQuoteImporting(isImporting);

    if (
      !isImporting &&
      user &&
      projectId &&
      currentView === "project" &&
      !isProjectLoading &&
      !isDeletingRef.current
    ) {
      const latestState = currentStateRef.current;

      setSetup(latestState.setup);
      setRates(latestState.rates);
      setAssetRates(latestState.assetRates);
      setShots(latestState.shots);
      setAssets(latestState.assets);
      setDocuments(latestState.documents);
      setPurchaseOrders(latestState.purchaseOrders);
      setVersions(latestState.versions);
      setActiveVersionId(latestState.activeVersionId);
      applyInferredShotCodeSettings(latestState.shots);

      void saveProjectState(
        projectId,
        latestState.setup,
        latestState.rates,
        latestState.assetRates,
        latestState.shots,
        latestState.assets,
        latestState.documents,
        latestState.purchaseOrders,
        user,
        false,
        latestState.versions,
        latestState.activeVersionId,
        false
      );
    }
  }, [currentView, isProjectLoading, projectId, user]);

  useEffect(() => {
    if (!user || !projectId || currentView !== "project" || isProjectLoading || isNewProjectRef.current) return;
    if (openingProjectId || lastOpenedProjectIdRef.current === projectId) return;
    
    // Preserve the current tab/route when refreshing the active project in the background.
    // Without this, the refresh path falls back to projectdetails and can bounce users
    // out of tabs like Documents after reload/snapshot churn.
    handleOpenProjectById(
      projectId,
      {
        activeTab,
        isProjectSettingsView,
      },
      "replace"
    );
  }, [user, projectId, currentView, isProjectLoading, openingProjectId, activeTab, isProjectSettingsView]);

  const handleProjectDataLoaded = (id: string, setup: ProjectSetup, rates: RateCard, assetRates: AssetRateCard, shots: Shot[], assets: Asset[], loadedDocs: ProjectDocument[] = [], loadedPurchaseOrders: PurchaseOrder[] = []) => {
    const setupWithShowColour = ensureProjectSetupShowColour(setup, id);
    lastOpenedProjectIdRef.current = id;
    setProjectId(id);
    handleSetupChange(setupWithShowColour);
    setRates({ ...DEFAULT_RATES, ...rates });
    setAssetRates({ ...DEFAULT_ASSET_RATES, ...assetRates });
    setShots(rehydrateShots(shots));
    setAssets(rehydrateAssets(assets));
    setDocuments(loadedDocs);
    setFptProjectId(null);
    projectFptMetadataRef.current = {};
    setScenes([]);
    setSequences([]);
    setShootingSchedules([]);
    setPurchaseOrders(loadedPurchaseOrders);
    
    // Initialize refs to prevent immediate re-save
    const CHUNK_SIZE = 50;
    const shotChunks: string[] = [];
    for (let i = 0; i < shots.length; i += CHUNK_SIZE) {
      shotChunks.push(JSON.stringify(shots.slice(i, i + CHUNK_SIZE)));
    }
    const assetChunks: string[] = [];
    for (let i = 0; i < assets.length; i += CHUNK_SIZE) {
      assetChunks.push(JSON.stringify(assets.slice(i, i + CHUNK_SIZE)));
    }
    
    const docMap: Record<string, string> = {};
    loadedDocs.forEach(d => {
      const { file, ...rest } = d;
      docMap[d.id] = JSON.stringify(rest);
    });

    const projectDataToCompare = JSON.stringify({
      projectName: (setupWithShowColour.projectName || "Untitled Project").substring(0, 99),
      clientName: setupWithShowColour.clientName,
      showCode: setupWithShowColour.showCode,
      bidDate: setupWithShowColour.bidDate,
      currency: setupWithShowColour.currency || "GBP",
      status: setupWithShowColour.status || "Bidding",
      projectStartEst: setupWithShowColour.projectStartEst,
      projectEndEst: setupWithShowColour.projectEndEst,
      setup: setupWithShowColour,
      rates: rates,
      assetRates: assetRates,
      latestTotalCost: calculateTotalProjectCost(shots, assets, setupWithShowColour, rates, assetRates),
    });

    lastSavedChunksRef.current = { shots: shotChunks, assets: assetChunks };
    lastSavedDocumentsRef.current = docMap;
    lastSavedProjectDataRef.current = projectDataToCompare;
    lastSavedDataRef.current = JSON.stringify({ setup: setupWithShowColour, rates, assetRates, shots, assets, documents: loadedDocs, scenes: [], sequences: [], shootingSchedules: [], purchaseOrders: loadedPurchaseOrders });
    lastRemoteSavedDataRef.current = JSON.stringify({ setup: setupWithShowColour, rates, assetRates, shots, assets, documents: loadedDocs, scenes: [], sequences: [], shootingSchedules: [], purchaseOrders: loadedPurchaseOrders });
    
    handleNavigate("project");
    handleProjectTabChange(lastProjectTabRef.current || "show-dashboard", "replace");
    setActiveVersionId(null);
  };

  const handleGoToDashboard = async () => {
    if (currentView === "project" && user && projectId) {
      setIsSaving(true);
      await saveProjectState(projectId, setup, rates, assetRates, shots, assets, documents, purchaseOrders, user, true);
      setIsSaving(false);
    }
    lastOpenedProjectIdRef.current = null;
    setProjectId(null);
    handleNavigate("dashboard");
  };

  const handleOpenProjectById = async (
    targetProjectId: string,
    routeOptions: Partial<Pick<AppRouteState, "activeTab" | "isProjectSettingsView">> = {},
    historyMode: "push" | "replace" = "push"
  ) => {
    if (!user || openingProjectId) return;
    routeSyncModeRef.current = historyMode;
    const preservedProjectTab =
      currentView === "project" && projectId === targetProjectId
        ? lastProjectTabRef.current
        : "show-dashboard";
    const targetTab = routeOptions.activeTab || preservedProjectTab || "show-dashboard";
    const targetSettingsView = routeOptions.isProjectSettingsView || false;
    setOpeningProjectId(targetProjectId);
    setIsProjectLoading(true);
    
    // 1. Check Local Cache First
    const localData = localStorage.getItem(getProjectCacheKey(targetProjectId));
    let navigated = false;
    if (localData) {
      try {
        const project = JSON.parse(localData);
        applyProjectData(project);
        lastOpenedProjectIdRef.current = targetProjectId;
        setIsProjectSettingsView(targetSettingsView);
        setActiveTab(targetSettingsView ? "projectdetails" : targetTab);
        setIsProjectLoading(false);
        setOpeningProjectId(null);
        handleNavigate("project", historyMode);
        navigated = true;
        // Continue to fetch from server in background to ensure latest
      } catch (e) {
        console.error("Failed to parse local project data:", e);
      }
    }

    try {
      let projectData = null;

      // Try fetching from Firestore directly first
      try {
        const docSnap = await getDoc(doc(db, 'projects', targetProjectId));
        if (docSnap.exists()) {
          projectData = { id: docSnap.id, ...docSnap.data() };
          
          // Fetch subcollections
          const [
            docsSnap,
            versionsSnap,
            shotsSnap,
            assetsSnap,
            scenesSnap,
            sequencesSnap,
          ] = await Promise.all([
            getDocs(collection(db, 'projects', targetProjectId, 'documents')),
            getDocs(collection(db, 'projects', targetProjectId, 'versions')),
            getDocs(collection(db, 'projects', targetProjectId, 'shots')),
            getDocs(collection(db, 'projects', targetProjectId, 'assets')),
            getDocs(collection(db, 'projects', targetProjectId, 'scenes')),
            getDocs(collection(db, 'projects', targetProjectId, 'sequences')),
          ]);
          projectData.documents = docsSnap.docs.map(d => d.data());
          projectData.versions = versionsSnap.docs.map(d => d.data());
          projectData.shots = shotsSnap.docs.map(d => d.data());
          projectData.assets = assetsSnap.docs.map(d => d.data());
          projectData.scenes = scenesSnap.docs.map(d => d.data());
          projectData.sequences = sequencesSnap.docs.map(d => d.data());
        }
      } catch (fsError) {
        console.error("Failed to fetch project from Firestore:", fsError);
      }

      // Fallback to server if not found in Firestore
      if (!projectData) {
        const res = await fetch(`/api/projects/${targetProjectId}`, {
          headers: { "x-user-uid": user?.uid || "" }
        });
        if (!res.ok) {
          if (!localData) {
            setOpeningProjectId(null);
            setIsProjectLoading(false);
            toast.error("Failed to load project");
          }
          return;
        }
        projectData = await res.json();
      }

      // If server/Firestore version is newer or we don't have local data, update
      const localProject = localData ? JSON.parse(localData) : null;
      
      let finalProjectData = projectData;
      if (localProject) {
        const localTime = new Date(localProject.updatedAt || localProject.createdAt || 0).getTime();
        const remoteTime = new Date(projectData.updatedAt || projectData.createdAt || 0).getTime();
        const localHasBidData =
          (Array.isArray(localProject.shots) && localProject.shots.length > 0) ||
          (Array.isArray(localProject.assets) && localProject.assets.length > 0) ||
          (Array.isArray(localProject.versions) && localProject.versions.length > 0);
        const remoteClearsBidData =
          Array.isArray(projectData.shots) &&
          Array.isArray(projectData.assets) &&
          Array.isArray(projectData.versions) &&
          projectData.shots.length === 0 &&
          projectData.assets.length === 0 &&
          projectData.versions.length === 0;
        
        if (remoteClearsBidData && localHasBidData) {
          console.warn(`Preserving local bid data for project ${targetProjectId} because remote snapshot contains empty shots/assets/versions`);
          finalProjectData = {
            ...projectData,
            ...localProject,
            shots: localProject.shots,
            assets: localProject.assets,
            versions: localProject.versions,
            activeVersionId: localProject.activeVersionId || projectData.activeVersionId,
            latestTotalCost: localProject.latestTotalCost || projectData.latestTotalCost,
            latestVersionName: localProject.latestVersionName || projectData.latestVersionName,
          };
        } else if (localTime > remoteTime) {
          console.log(`Using local project data (fresher: ${localTime} > ${remoteTime})`);
          // Merge but prefer local for shots/assets if they have thumbnails
          finalProjectData = {
            ...projectData,
            ...localProject,
            // Deep merge shots to ensure we keep thumbnails if they exist locally
            shots: projectData.shots?.map((s: any) => {
              const localShot = localProject.shots?.find((ls: any) => ls.id === s.id);
              return (localShot?.thumbnail && !s.thumbnail) ? { ...s, thumbnail: localShot.thumbnail } : s;
            }) || projectData.shots
          };
        } else {
          // Merge but prefer remote
          finalProjectData = {
            ...localProject,
            ...projectData
          };
        }
      }

      applyProjectData(finalProjectData);
      lastOpenedProjectIdRef.current = targetProjectId;
      if (!navigated) {
        setIsProjectSettingsView(targetSettingsView);
        setActiveTab(targetSettingsView ? "projectdetails" : targetTab);
      }
      persistProjectCache(targetProjectId, finalProjectData);

      setIsProjectLoading(false);
      setOpeningProjectId(null);
      if (!navigated) {
        handleNavigate("project", historyMode);
      }
    } catch (error) {
      console.error("Error fetching project:", error);
      if (!localData) {
        setIsProjectLoading(false);
        setOpeningProjectId(null);
        toast.error("Failed to load project");
      }
    }
  };

  const applyRouteState = useCallback(async (route: AppRouteState) => {
    if (typeof window === "undefined") return;

    isApplyingRouteRef.current = true;

    try {
      if (route.view === "project" && route.projectId) {
        pendingRouteRef.current = {
          projectId: route.projectId,
          activeTab: route.activeTab,
          isProjectSettingsView: route.isProjectSettingsView,
        };

        if (projectId === route.projectId) {
          routeSyncModeRef.current = "replace";
          setCurrentView("project");
          setIsProjectSettingsView(route.isProjectSettingsView);
          setActiveTab(route.isProjectSettingsView ? "projectdetails" : route.activeTab);
        } else if (user) {
          await handleOpenProjectById(
            route.projectId,
            {
              activeTab: route.activeTab,
              isProjectSettingsView: route.isProjectSettingsView,
            },
            "replace"
          );
        }
        return;
      }

      pendingRouteRef.current = null;

      routeSyncModeRef.current = "replace";
      if (route.view === "dashboard") {
        setProjectId(null);
      }
      handleNavigate(route.view, "replace");
    } finally {
      window.setTimeout(() => {
        isApplyingRouteRef.current = false;
      }, 0);
    }
  }, [handleNavigate, handleOpenProjectById, projectId, user]);

  const applyProjectData = (projectData: any) => {
    const newSetup = ensureProjectSetupShowColour(projectData.setup || {
      projectName: projectData.projectName || "Untitled Project",
      clientName: projectData.clientName || "",
      showCode: projectData.showCode || "",
      bidDate: projectData.bidDate || new Date().toISOString().split("T")[0],
      onsetSupervision: projectData.onsetSupervision || 0,
      currency: projectData.currency || "GBP",
      targetCurrency: projectData.targetCurrency || "",
      exchangeRate: projectData.exchangeRate || 1,
      person: projectData.person || "",
      assumptions: projectData.assumptions || "",
      thumbnail: projectData.thumbnail,
    }, projectData.id || projectData.projectName);

    if (projectData.setup && projectData.thumbnail) {
      newSetup.thumbnail = projectData.thumbnail;
    }

    const newRates = projectData.rates || currentStateRef.current.rates;
    const newAssetRates = projectData.assetRates || currentStateRef.current.assetRates;
    const newShots: Shot[] = projectData.shots !== undefined ? projectData.shots : currentStateRef.current.shots;
    const newAssets: Asset[] = projectData.assets !== undefined ? projectData.assets : currentStateRef.current.assets;
    const loadedDocuments: ProjectDocument[] = projectData.documents !== undefined ? projectData.documents : currentStateRef.current.documents;
    const loadedScenes: Scene[] = projectData.scenes !== undefined ? projectData.scenes : currentStateRef.current.scenes;
    const loadedSequences: Sequence[] = projectData.sequences !== undefined ? projectData.sequences : currentStateRef.current.sequences;
    const loadedShootingSchedules: ShootingSchedule[] = projectData.shootingSchedules !== undefined ? projectData.shootingSchedules : currentStateRef.current.shootingSchedules;
    const loadedPurchaseOrders: PurchaseOrder[] = projectData.purchaseOrders !== undefined ? projectData.purchaseOrders : currentStateRef.current.purchaseOrders;
    let newVersions: BidVersion[] = projectData.versions !== undefined ? projectData.versions : currentStateRef.current.versions;
    let newActiveVersionId = projectData.activeVersionId !== undefined ? projectData.activeVersionId : currentStateRef.current.activeVersionId;
    if (!newActiveVersionId && newVersions.length > 0) {
      const latestVersion = [...newVersions].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
      newActiveVersionId = latestVersion.id;
    }
    
    projectFptMetadataRef.current = {
      fptProjectId: projectData.fptProjectId,
      fptProjectName: projectData.fptProjectName,
      fptProjectUrl: projectData.fptProjectUrl,
      fptLinked: projectData.fptLinked,
      lastPushedAt: projectData.lastPushedAt,
      lastPulledAt: projectData.lastPulledAt,
      lastSyncedBidVersion: projectData.lastSyncedBidVersion,
      syncStatus: projectData.syncStatus,
      lastFptSyncAt: projectData.lastFptSyncAt,
      fptSyncHash: projectData.fptSyncHash,
      entitySyncMetadata: projectData.entitySyncMetadata,
      identityMappings: projectData.identityMappings,
      financials: projectData.financials,
      profitability: projectData.profitability,
      fptStructureInspection: projectData.fptStructureInspection,
    };

    if (projectData.id) setProjectId(projectData.id);
    setFptProjectId(projectData.fptProjectId ? String(projectData.fptProjectId) : null);
    handleSetupChange(newSetup);
    if (projectData.rates) setRates(newRates);
    if (projectData.assetRates) setAssetRates(newAssetRates);
    if (projectData.shots !== undefined) setShots(newShots);
    if (projectData.assets !== undefined) setAssets(newAssets);
    if (projectData.documents !== undefined) setDocuments(loadedDocuments);
    if (projectData.scenes !== undefined) setScenes(loadedScenes);
    if (projectData.sequences !== undefined) setSequences(loadedSequences);
    if (projectData.shootingSchedules !== undefined) setShootingSchedules(loadedShootingSchedules);
    if (projectData.purchaseOrders !== undefined) setPurchaseOrders(loadedPurchaseOrders);
    if (projectData.versions !== undefined) setVersions(newVersions);
    if (newActiveVersionId) setActiveVersionId(newActiveVersionId);
    
    lastSavedDataRef.current = JSON.stringify({ 
      setup: newSetup, 
      rates: newRates, 
      assetRates: newAssetRates, 
      shots: newShots,
      assets: newAssets,
      documents: loadedDocuments,
      scenes: loadedScenes,
      sequences: loadedSequences,
      shootingSchedules: loadedShootingSchedules,
      purchaseOrders: loadedPurchaseOrders
    });
    lastRemoteSavedDataRef.current = JSON.stringify({
      setup: newSetup,
      rates: newRates,
      assetRates: newAssetRates,
      shots: newShots,
      assets: newAssets,
      documents: loadedDocuments,
      scenes: loadedScenes,
      sequences: loadedSequences,
      shootingSchedules: loadedShootingSchedules,
      purchaseOrders: loadedPurchaseOrders
    });
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (loading) {
      setHasHydratedInitialRoute(false);
      return;
    }

    let cancelled = false;

    const hydrateInitialRoute = async () => {
      setHasHydratedInitialRoute(false);
      await applyRouteState(parseRoutePath(window.location.pathname));
      if (!cancelled) {
        setHasHydratedInitialRoute(true);
      }
    };

    void hydrateInitialRoute();

    return () => {
      cancelled = true;
    };
  }, [applyRouteState, loading, user?.uid]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handlePopState = () => {
      applyRouteState(parseRoutePath(window.location.pathname));
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [applyRouteState]);

  // Real-time project sync
  useEffect(() => {
    if (DISABLE_CLIENT_FIRESTORE_WATCHERS) return;
    if (!user || !projectId || isProjectLoading || isDeletingRef.current) return;
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;

    void import("firebase/firestore").then(({ getFirestore, onSnapshot, doc: realtimeDoc }) => {
      if (cancelled) return;

      const realtimeDb = getFirestore(firebaseApp);
      const projectRef = realtimeDoc(realtimeDb, 'projects', projectId);
      unsubscribe = onSnapshot(projectRef, (snapshot) => {
        if (!snapshot.exists()) return;

        const projectData = snapshot.data();

        // Construct comparison string using only fields present in the main document snapshot
        // This prevents loops and data loss since documents/shots/assets are in subcollections
        const incomingSyncData = {
          setup: projectData.setup,
          rates: projectData.rates,
          assetRates: projectData.assetRates,
        };

        const currentSyncData = {
          setup: currentStateRef.current.setup,
          rates: currentStateRef.current.rates,
          assetRates: currentStateRef.current.assetRates,
        };

        if (JSON.stringify(incomingSyncData) !== JSON.stringify(currentSyncData)) {
          console.log("Real-time update received from Firestore");
          applyProjectData(projectData);
          // Also update local storage
          try {
            persistProjectCache(projectId, {
              ...currentStateRef.current,
              ...projectData
            });
          } catch (storageError) {
            console.warn("Project cache sync failed during real-time update", storageError);
          }
        }
      }, (error) => {
        // Ignore permission denied errors if user is not owner (they shouldn't be syncing anyway)
        if (error.code !== 'permission-denied') {
          console.error("Firestore sync error:", error);
        }
      });
    }).catch((error) => {
      console.error("Failed to load Firestore realtime sync module:", error);
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [projectId, user, isProjectLoading]);

  const handleDeleteProject = async () => {
    if (!projectId) return;
    
    isDeletingRef.current = true;
    try {
      // Rely on server API for comprehensive deletion (trash + subcollections)
      const res = await fetch(`/api/projects/${projectId}`, {
        method: "DELETE",
        headers: {
          "x-user-uid": user?.uid || ""
        }
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        console.error("Server delete failed:", errorData);
        // If server failed, try a direct Firestore delete as fallback
        try {
          await deleteDoc(doc(db, 'projects', projectId));
        } catch (fsError) {
          console.error("Fallback Firestore delete also failed:", fsError);
        }
      }

      const deletedProjectId = projectId;

      // Clear local state and go back to dashboard
      localStorage.removeItem("vfx_project_id");
      localStorage.removeItem("vfx_setup");
      localStorage.removeItem("vfx_rates");
      localStorage.removeItem("vfx_asset_rates");
      localStorage.removeItem("vfx_shots");
      localStorage.removeItem("vfx_assets");
      localStorage.removeItem("vfx_documents");
      
      setProjectId(null);
      handleSetupChange(defaultProjectSetup);
      setRates(DEFAULT_RATES);
      setAssetRates(DEFAULT_ASSET_RATES);
      setShots([]);
      setAssets([]);
      setDocuments([]);
      setScenes([]);
      setSequences([]);
      setShootingSchedules([]);
      setScenes([]);
      setSequences([]);
      setPurchaseOrders([]);
      handleNavigate("dashboard");
      setIsDeleteProjectConfirmOpen(false);
      closeProjectSettings("replace");

      toast.success("Project moved to trash", {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              const restoreRes = await fetch(`/api/projects/${deletedProjectId}/restore`, {
                method: "POST",
                headers: { "x-user-uid": user?.uid || "" }
              });
              if (restoreRes.ok) {
                handleOpenProjectById(deletedProjectId);
                toast.success("Project restored");
              } else {
                toast.error("Failed to restore project");
              }
            } catch (err) {
              console.error("Failed to undo project deletion:", err);
              toast.error("Failed to restore project");
            }
          }
        }
      });

      // Reset deleting flag after state updates have settled
      setTimeout(() => {
        isDeletingRef.current = false;
      }, 500);
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error("Failed to delete project");
      setIsDeleteProjectConfirmOpen(false);
      closeProjectSettings("replace");
      handleNavigate("dashboard");
      isDeletingRef.current = false;
    }
  };

  const handleBackToDashboard = async () => {
    if (user && projectId) {
      setIsSaving(true);
      await saveProjectState(projectId, setup, rates, assetRates, shots, assets, documents, purchaseOrders, user, true);
      setIsSaving(false);
    }
    localStorage.removeItem("vfx_project_id");
    setProjectId(null);
    handleNavigate(previousView);
  };

  const findDuplicateValue = useCallback((values: string[]) => {
    const seen = new Set<string>();
    for (const value of values) {
      const normalized = value.trim().toLowerCase();
      if (!normalized) continue;
      if (seen.has(normalized)) return value.trim();
      seen.add(normalized);
    }
    return null;
  }, []);

  const buildUniqueCode = useCallback((baseCode: string, existingCodes: string[]) => {
    const normalizedExisting = new Set(existingCodes.map((code) => code.trim().toLowerCase()));
    if (!normalizedExisting.has(baseCode.trim().toLowerCase())) return baseCode;

    let counter = 2;
    while (normalizedExisting.has(`${baseCode}_${counter}`.trim().toLowerCase())) {
      counter += 1;
    }
    return `${baseCode}_${counter}`;
  }, []);

  const hasMeaningfulText = (value?: string | null) => Boolean(value && value.trim() !== "");

  const mergeImportedShotRecord = useCallback((existingShot: Shot, importedShot: Shot): Shot => {
    const shouldReplaceStatus =
      importedShot.status !== "Bidding" || existingShot.status === "Bidding";
    const nextStatus = shouldReplaceStatus ? importedShot.status : existingShot.status;
    const nextDays = Object.fromEntries(
      Object.keys(existingShot.days).map((key) => {
        const typedKey = key as keyof Shot["days"];
        const importedValue = importedShot.days?.[typedKey] ?? 0;
        return [typedKey, importedValue > 0 ? importedValue : existingShot.days?.[typedKey] ?? 0];
      })
    ) as unknown as Shot["days"];

    return {
      ...existingShot,
      ...importedShot,
      id: existingShot.id,
      status: nextStatus,
      active: shouldReplaceStatus ? nextStatus !== "Lost" && nextStatus !== "Off" : existingShot.active,
      thumbnail: existingShot.thumbnail || importedShot.thumbnail,
      description: hasMeaningfulText(importedShot.description) ? importedShot.description : existingShot.description,
      sceneDescription: hasMeaningfulText(importedShot.sceneDescription) ? importedShot.sceneDescription : existingShot.sceneDescription,
      scopeOfWork: hasMeaningfulText(importedShot.scopeOfWork) ? importedShot.scopeOfWork : existingShot.scopeOfWork,
      assumptions: hasMeaningfulText(importedShot.assumptions) ? importedShot.assumptions : existingShot.assumptions,
      internalNotes: hasMeaningfulText(importedShot.internalNotes) ? importedShot.internalNotes : existingShot.internalNotes,
      frames: importedShot.frames > 0 ? importedShot.frames : existingShot.frames,
      quantity: importedShot.quantity > 1 || existingShot.quantity === 1 ? importedShot.quantity : existingShot.quantity,
      legacyPrice: importedShot.legacyPrice !== undefined ? importedShot.legacyPrice : existingShot.legacyPrice,
      days: nextDays,
    };
  }, []);

  const mergeImportedAssetRecord = useCallback((existingAsset: Asset, importedAsset: Asset): Asset => {
    const shouldReplaceStatus =
      importedAsset.status !== "Bidding" || existingAsset.status === "Bidding";
    const nextStatus = shouldReplaceStatus ? importedAsset.status : existingAsset.status;
    const nextDays = Object.fromEntries(
      Object.keys(existingAsset.days).map((key) => {
        const typedKey = key as keyof Asset["days"];
        const importedValue = importedAsset.days?.[typedKey] ?? 0;
        return [typedKey, importedValue > 0 ? importedValue : existingAsset.days?.[typedKey] ?? 0];
      })
    ) as unknown as Asset["days"];

    return {
      ...existingAsset,
      ...importedAsset,
      id: existingAsset.id,
      status: nextStatus,
      active: shouldReplaceStatus ? nextStatus !== "Lost" && nextStatus !== "Off" : existingAsset.active,
      thumbnail: existingAsset.thumbnail || importedAsset.thumbnail,
      type: hasMeaningfulText(importedAsset.type) ? importedAsset.type : existingAsset.type,
      description: hasMeaningfulText(importedAsset.description) ? importedAsset.description : existingAsset.description,
      scopeOfWork: hasMeaningfulText(importedAsset.scopeOfWork) ? importedAsset.scopeOfWork : existingAsset.scopeOfWork,
      assumptions: hasMeaningfulText(importedAsset.assumptions) ? importedAsset.assumptions : existingAsset.assumptions,
      internalNotes: hasMeaningfulText(importedAsset.internalNotes) ? importedAsset.internalNotes : existingAsset.internalNotes,
      quantity: importedAsset.quantity > 1 || existingAsset.quantity === 1 ? importedAsset.quantity : existingAsset.quantity,
      legacyPrice: importedAsset.legacyPrice !== undefined ? importedAsset.legacyPrice : existingAsset.legacyPrice,
      days: nextDays,
    };
  }, []);

  const applyInferredShotCodeSettings = useCallback((shotList: Shot[]) => {
    const inferred = inferShotCodeSettingsFromCodes(shotList.map((shot) => shot.shotCode));
    if (!inferred) return;

    handleSetupChange((prev) => {
      const nextShowCode = inferred.showCode || prev.showCode;
      const nextBreadcrumb = inferred.shotCodeBreadcrumb;
      const nextPattern = inferred.shotCodePattern;
      const parsedSegments = nextBreadcrumb.split("_");
      const nextProjectType =
        parsedSegments.length === 4
          ? "episodic"
          : prev.projectType === "episodic"
            ? "feature_film"
            : (prev.projectType || "feature_film");
      if (
        prev.showCode === nextShowCode &&
        prev.shotCodeBreadcrumb === nextBreadcrumb &&
        prev.shotCodePattern === nextPattern &&
        prev.projectType === nextProjectType
      ) {
        return prev;
      }
      return {
        ...prev,
        showCode: nextShowCode,
        projectType: nextProjectType,
        shotCodeBreadcrumb: nextBreadcrumb,
        shotCodePattern: nextPattern,
      };
    });
  }, [handleSetupChange]);

  useEffect(() => {
    if (shots.length === 0) return;
    if (isQuoteImporting) return;
    if (setup.showCode && setup.shotCodeBreadcrumb) return;
    applyInferredShotCodeSettings(shots);
  }, [shots, setup.showCode, setup.shotCodeBreadcrumb, applyInferredShotCodeSettings, isQuoteImporting]);

  const handleShotsChange = useCallback((update: Shot[] | ((prev: Shot[]) => Shot[])) => {
    setShots((prev) => {
      const next = typeof update === "function" ? update(prev) : update;
      const duplicate = findDuplicateValue(next.map((shot) => shot.shotCode || ""));
      if (duplicate) {
        toast.error(`Shot code ${duplicate} already exists in this project.`);
        return prev;
      }
      return next;
    });
    setActiveVersionId(null);
  }, [findDuplicateValue]);

  const handleAssetsChange = useCallback((update: Asset[] | ((prev: Asset[]) => Asset[])) => {
    setAssets((prev) => {
      const next = typeof update === "function" ? update(prev) : update;
      const duplicate = findDuplicateValue(next.map((asset) => asset.assetCode || ""));
      if (duplicate) {
        toast.error(`Asset code ${duplicate} already exists in this project.`);
        return prev;
      }
      return next;
    });
    setActiveVersionId(null);
  }, [findDuplicateValue]);

  const handleAddShot = useCallback(() => {
    setShots(prev => {
      const currentShots = prev || [];
      const baseCode = buildPlaceholderShotCode({
        showCode: setup.showCode,
        breadcrumb: setup.shotCodeBreadcrumb,
        existingCodes: currentShots.map((shot) => shot.shotCode),
      });
      const newShot: Shot = {
        id: uuidv4(),
        active: true,
        status: "Bidding",
        shotCode: baseCode,
        description: "",
        scopeOfWork: "",
        frames: 0,
        quantity: 1,
        assumptions: "",
        internalNotes: "",
        days: { 
          supervisor: 0, concept: 0, dmp: 0, comp: 0, roto: 0, prep: 0, gfx: 0, 
          cameraTR: 0, objectTR: 0, rANIM: 0, layout: 0, 
          anim: 0, light: 0, fx: 0
        },
      };
      return [...currentShots, newShot];
    });
    setActiveVersionId(null);
  }, [setup.showCode, setup.shotCodeBreadcrumb]);

  const handleDuplicateShot = useCallback((id: string) => {
    setShots(prev => {
      const shotToDuplicate = prev.find((s) => s.id === id);
      if (!shotToDuplicate) return prev;
      const duplicatedCode = buildUniqueCode(`${shotToDuplicate.shotCode}_copy`, prev.map((shot) => shot.shotCode));

      const newShot: Shot = {
        ...shotToDuplicate,
        id: uuidv4(),
        shotCode: duplicatedCode,
      };
      
      const index = prev.findIndex((s) => s.id === id);
      const newShots = [...prev];
      newShots.splice(index + 1, 0, newShot);
      return newShots;
    });
    setActiveVersionId(null);
  }, [buildUniqueCode]);

  const handleDeleteShot = useCallback((id: string) => {
    setShots(prev => {
      const shotToDelete = prev.find(s => s.id === id);
      if (!shotToDelete) return prev;
      const index = prev.findIndex(s => s.id === id);
      
      toast.success(`Shot ${shotToDelete.shotCode} deleted`, {
        action: {
          label: "Undo",
          onClick: () => {
            setShots(current => {
              const newShots = [...current];
              newShots.splice(index, 0, shotToDelete);
              return newShots;
            });
          }
        }
      });
      
      return prev.filter((s) => s.id !== id);
    });
    setActiveVersionId(null);
  }, []);

  const handleAddAsset = useCallback(() => {
    setAssets(prev => {
      const currentAssets = prev || [];
      const baseCode = `AST_${String(currentAssets.length + 1).padStart(4, "0")}`;
      const newAsset: Asset = {
        id: uuidv4(),
        active: true,
        status: "Bidding",
        assetCode: buildUniqueCode(baseCode, currentAssets.map((asset) => asset.assetCode)),
        description: "",
        scopeOfWork: "",
        quantity: 1,
        assumptions: "",
        internalNotes: "",
        days: { 
          supervisor: 0, concept: 0, dmp: 0, model: 0, rig: 0, retop: 0, 
          cloth: 0, fur: 0, groom: 0, texture: 0, 
          anim: 0, fx: 0, lookdev: 0, photoscan: 0
        },
      };
      return [...currentAssets, newAsset];
    });
    setActiveVersionId(null);
  }, [buildUniqueCode]);

  const handleDuplicateAsset = useCallback((id: string) => {
    setAssets(prev => {
      const assetToDuplicate = prev.find((a) => a.id === id);
      if (!assetToDuplicate) return prev;
      const duplicatedCode = buildUniqueCode(`${assetToDuplicate.assetCode}_copy`, prev.map((asset) => asset.assetCode));

      const newAsset: Asset = {
        ...assetToDuplicate,
        id: uuidv4(),
        assetCode: duplicatedCode,
      };
      
      const index = prev.findIndex((a) => a.id === id);
      const newAssets = [...prev];
      newAssets.splice(index + 1, 0, newAsset);
      return newAssets;
    });
    setActiveVersionId(null);
  }, [buildUniqueCode]);

  const handleDeleteAsset = useCallback((id: string) => {
    setAssets(prev => {
      const assetToDelete = prev.find(a => a.id === id);
      if (!assetToDelete) return prev;
      const index = prev.findIndex(a => a.id === id);
      
      toast.success(`Asset ${assetToDelete.assetCode} deleted`, {
        action: {
          label: "Undo",
          onClick: () => {
            setAssets(current => {
              const newAssets = [...current];
              newAssets.splice(index, 0, assetToDelete);
              return newAssets;
            });
          }
        }
      });
      
      return prev.filter((a) => a.id !== id);
    });
    setActiveVersionId(null);
  }, []);

  const handleImportShots = async (newShots: Shot[], merge: boolean) => {
    let updatedShots: Shot[];
    const baseShots = quoteImportInProgressRef.current
      ? (Array.isArray(currentStateRef.current.shots) ? currentStateRef.current.shots : [])
      : shots;
    if (merge) {
      updatedShots = [...baseShots];
      newShots.forEach(newShot => {
        const existingIndex = updatedShots.findIndex(s => 
          s.shotCode.trim().toLowerCase() === newShot.shotCode.trim().toLowerCase()
        );
        if (existingIndex !== -1) {
          updatedShots[existingIndex] = mergeImportedShotRecord(updatedShots[existingIndex], newShot);
        } else {
          updatedShots.push(newShot);
        }
      });
    } else {
      const dedupedShots = new Map<string, Shot>();
      newShots.forEach(newShot => {
        dedupedShots.set(newShot.shotCode.trim().toLowerCase(), newShot);
      });
      updatedShots = Array.from(dedupedShots.values());
    }
    
    currentStateRef.current = { ...currentStateRef.current, shots: updatedShots };
    if (quoteImportInProgressRef.current) {
      lastSavedDataRef.current = "";
      lastRemoteSavedDataRef.current = "";
    } else {
      setShots(updatedShots);
      applyInferredShotCodeSettings(updatedShots);
    }
    setShotImportRevision((prev) => prev + 1);
  };

  const handleImportAssets = async (newAssets: Asset[], merge: boolean) => {
    let updatedAssets: Asset[];
    const baseAssets = quoteImportInProgressRef.current
      ? (Array.isArray(currentStateRef.current.assets) ? currentStateRef.current.assets : [])
      : assets;
    if (merge) {
      updatedAssets = [...baseAssets];
      newAssets.forEach(newAsset => {
        const existingIndex = updatedAssets.findIndex(a => 
          a.assetCode.trim().toLowerCase() === newAsset.assetCode.trim().toLowerCase()
        );
        if (existingIndex !== -1) {
          updatedAssets[existingIndex] = mergeImportedAssetRecord(updatedAssets[existingIndex], newAsset);
        } else {
          updatedAssets.push(newAsset);
        }
      });
    } else {
      const dedupedAssets = new Map<string, Asset>();
      newAssets.forEach(newAsset => {
        dedupedAssets.set(newAsset.assetCode.trim().toLowerCase(), newAsset);
      });
      updatedAssets = Array.from(dedupedAssets.values());
    }
    
    currentStateRef.current = { ...currentStateRef.current, assets: updatedAssets };
    if (quoteImportInProgressRef.current) {
      lastSavedDataRef.current = "";
      lastRemoteSavedDataRef.current = "";
    } else {
      setAssets(updatedAssets);
    }
  };

  const handleImportOnset = async (onsetSupervision: number, merge: boolean) => {
    const updatedSetup = {
      ...setup,
      onsetSupervision: onsetSupervision > 0 ? onsetSupervision : (merge ? (setup.onsetSupervision || 0) : 0)
    };
    currentStateRef.current = { ...currentStateRef.current, setup: updatedSetup };
      handleSetupChange(updatedSetup);
  };

  const handleImportTopSheet = async (topSheet: TopSheet) => {
    const updatedSetup = {
      ...setup,
      topSheet: {
        ...(setup.topSheet || {}),
        ...topSheet,
        ...buildTopSheetDerivedData({
          ...(setup.topSheet || {}),
          ...topSheet
        })
      }
    };
    currentStateRef.current = { ...currentStateRef.current, setup: updatedSetup };
    handleSetupChange(updatedSetup);
  };

  const handleImportShootingSchedule = useCallback((incomingSchedule: ShootingSchedule) => {
    const now = new Date().toISOString();
    const updatedSchedules = currentStateRef.current.shootingSchedules
      .map((schedule) =>
        schedule.status === "confirmed" && incomingSchedule.status === "confirmed"
          ? { ...schedule, status: "superseded" as const, updatedAt: now }
          : schedule,
      )
      .filter((schedule) => schedule.id !== incomingSchedule.id);

    const nextSchedules = [
      ...updatedSchedules,
      {
        ...incomingSchedule,
        status: "confirmed" as const,
        updatedAt: now,
      },
    ].sort((left, right) => (right.importedAt || "").localeCompare(left.importedAt || ""));

    currentStateRef.current = { ...currentStateRef.current, shootingSchedules: nextSchedules };
    setShootingSchedules(nextSchedules);
    handleProjectTabChange("calendar");
    if (projectId && user) {
      void saveProjectState(
        projectId,
        currentStateRef.current.setup,
        currentStateRef.current.rates,
        currentStateRef.current.assetRates,
        currentStateRef.current.shots,
        currentStateRef.current.assets,
        currentStateRef.current.documents,
        currentStateRef.current.purchaseOrders,
        user,
        true,
        currentStateRef.current.versions,
        currentStateRef.current.activeVersionId,
        true
      );
    }
    toast.success("Shooting schedule imported into the project calendar.");
  }, [handleProjectTabChange, projectId, user]);

  useEffect(() => {
    const existingTopSheet = setup.topSheet || {};
    const derivedTopSheet = buildTopSheetDerivedData(existingTopSheet);
    const nextTopSheet = {
      ...existingTopSheet,
      ...derivedTopSheet
    };

    if (JSON.stringify(existingTopSheet.calculated || {}) !== JSON.stringify(nextTopSheet.calculated || {}) ||
        JSON.stringify(existingTopSheet.comparison || {}) !== JSON.stringify(nextTopSheet.comparison || {})) {
      handleSetupChange(prev => ({
        ...prev,
        topSheet: {
          ...(prev.topSheet || {}),
          ...derivedTopSheet
        }
      }));
    }
  }, [shots, assets, setup.onsetSupervision, setup.supervisionPercentage, setup.currency, rates, assetRates]);

  const exportVersionCode = useMemo(
    () => getCurrentWorkingVersionCode(versions, setup.version),
    [versions, setup.version]
  );
  const exportVersionName = useMemo(
    () =>
      formatBidVersionDisplayName({
        versionCode: exportVersionCode,
        projectName: setup.projectName,
        bidStatus: setup.bidStatus,
        date: new Date(),
      }),
    [exportVersionCode, setup.projectName, setup.bidStatus]
  );

  const handleAddPurchaseOrder = useCallback((purchaseOrder: Omit<PurchaseOrder, "id" | "remainingAmount">) => {
    const amount = purchaseOrder.amount || 0;
    const invoicedAmountToDate = purchaseOrder.invoicedAmountToDate || 0;
    const newPurchaseOrder: PurchaseOrder = {
      ...purchaseOrder,
      id: uuidv4(),
      remainingAmount: Math.max(0, amount - invoicedAmountToDate),
    };

    setPurchaseOrders((prev) => [...prev, newPurchaseOrder]);
    currentStateRef.current = {
      ...currentStateRef.current,
      purchaseOrders: [...currentStateRef.current.purchaseOrders, newPurchaseOrder]
    };
    toast.success(`PO ${newPurchaseOrder.poNumber} linked`);
  }, []);

  const createVersionSnapshotItems = (scopeType: ExportScopeType) => {
    const shotIds = scopeType === "subset" && shotExportFilter?.isActive
      ? new Set(shotExportFilter.filteredIds)
      : null;
    const assetIds = scopeType === "subset" && assetExportFilter?.isActive
      ? new Set(assetExportFilter.filteredIds)
      : null;

    const exportedShots = shotIds ? shots.filter((shot) => shotIds.has(shot.id)) : shots;
    const exportedAssets = assetIds ? assets.filter((asset) => assetIds.has(asset.id)) : assets;

    return {
      exportedShots,
      exportedAssets,
    };
  };

  const handleCreateExportVersion = async (scopeType: ExportScopeType, subsetOnsetDays: number) => {
    if (!user || !projectId) return;

    try {
      setIsSaving(true);

      const { exportedShots, exportedAssets } = createVersionSnapshotItems(scopeType);
      const exportSetup: ProjectSetup = {
        ...setup,
        version: exportVersionCode,
        onsetSupervision: scopeType === "subset" ? subsetOnsetDays : setup.onsetSupervision,
      };

      const scopeSnapshot = {
        scopeType,
        shotFilter: shotExportFilter || undefined,
        assetFilter: assetExportFilter || undefined,
        onsetDays: exportSetup.onsetSupervision || 0,
      };

      const response = await fetch(`/api/projects/${projectId}/export-version`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
        },
        body: JSON.stringify({
          setup: exportSetup,
          versionCode: exportVersionCode,
          versionName: exportVersionName,
          rates,
          assetRates,
          shots: exportedShots,
          assets: exportedAssets,
          scopeType,
          scopeSnapshot,
          createdBy: profile?.displayName || user.displayName || user.email || "Unknown",
        }),
      });

      if (!response.ok) {
        throw new Error((await response.json().catch(() => ({}))).error || "Failed to export bid version");
      }

      const newVersion: BidVersion = await response.json();
      const updatedVersions = [...versions, newVersion];
      const advancedWorkingVersion = getNextVersionCode(updatedVersions);
      setVersions(updatedVersions);
      handleSetupChange((prev) => ({ ...prev, version: advancedWorkingVersion }));
      currentStateRef.current = {
        ...currentStateRef.current,
        versions: updatedVersions,
        setup: { ...currentStateRef.current.setup, version: advancedWorkingVersion },
      };

      await saveProjectState(
        projectId,
        { ...setup, version: advancedWorkingVersion },
        rates,
        assetRates,
        shots,
        assets,
        documents,
        purchaseOrders,
        user,
        true,
        updatedVersions,
        activeVersionId,
        false
      );

      toast.success(`Exported locked bid version ${newVersion.versionName || newVersion.versionCode}. Working quote is now ${advancedWorkingVersion}.`);
      handleProjectTabChange("exports");
    } catch (error: any) {
      console.error("Error exporting bid version:", error);
      toast.error(error.message || "Failed to export bid version");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevertVersion = (version: BidVersion) => {
    setVersionToRevert(version);
  };

  const handleDeleteVersion = (versionId: string) => {
    setVersionToDelete(versionId);
  };

  const confirmDeleteVersion = async () => {
    if (versionToDelete && user && projectId) {
      const lockedVersion = versions.find((version) => version.id === versionToDelete);
      if (lockedVersion?.locked) {
        toast.error("Locked bid versions cannot be deleted");
        setVersionToDelete(null);
        return;
      }
      try {
        // Direct Firestore delete
        try {
          await deleteDoc(doc(db, 'projects', projectId, 'versions', versionToDelete));
        } catch (e) {
          console.error("Failed to delete version from Firestore", e);
        }

        const updatedVersions = versions.filter(v => v.id !== versionToDelete);
        const updatedActiveVersionId = activeVersionId === versionToDelete ? null : activeVersionId;
        setVersions(updatedVersions);
        setActiveVersionId(updatedActiveVersionId);
        setVersionToDelete(null);
        toast.success("Version deleted successfully");
        
        // Save project state with updated versions
        await saveProjectState(
          projectId,
          setup,
          rates,
          assetRates,
          shots,
          assets,
          documents,
          purchaseOrders,
          user,
          true,
          updatedVersions,
          updatedActiveVersionId
        );
      } catch (error) {
        console.error("Error deleting version:", error);
        toast.error("Failed to delete version");
      }
    }
  };

  const confirmRevertVersion = async () => {
    if (versionToRevert) {
      if (versionToRevert.locked) {
        toast.error("Locked bid versions are read-only historical snapshots");
        setVersionToRevert(null);
        return;
      }
      const shotsToRevert = versionToRevert.shots || [];
      const assetsToRevert = versionToRevert.assets || [];

      handleSetupChange({
        ...versionToRevert.setup,
        assumptions: versionToRevert.setup.assumptions || defaultProjectSetup.assumptions
      });
      setRates(versionToRevert.rates);
      setAssetRates(versionToRevert.assetRates || DEFAULT_ASSET_RATES);
      setShots(rehydrateShots(shotsToRevert));
      setAssets(rehydrateAssets(assetsToRevert));
      setActiveVersionId(versionToRevert.id);
      setVersionToRevert(null);
      toast.success(`Reverted to version ${versionToRevert.versionName}`);
    }
  };

  const settingsControls = useAnimation();

  const handleSettingsMouseEnter = () => {
    settingsControls.start({ rotate: 90, scale: 1.1, transition: { duration: 0.4, ease: "easeInOut" } });
  };

  const handleSettingsMouseLeave = () => {
    settingsControls.start({ rotate: 0, scale: 1, transition: { duration: 0.3, ease: "easeInOut" } });
  };

  const canEdit = isAdmin || isStandard;
  const canManageProjectSettings = isAdmin || hasPermission("project_settings.manage");
  const canViewFinance = effectiveProfile?.role === "admin" || effectiveProfile?.role === "super_admin";
  const canViewPeopleHr = effectiveProfile?.role === "operations" || effectiveProfile?.role === "super_admin";
  const canViewWiki = hasPermission("wiki.view");
  const showShellBackButton = currentView !== "welcome" && currentView !== "dashboard";
  const peopleProfileBackHandlerRef = useRef<(() => void) | null>(null);

  const handlePeopleProfileOpenChange = useCallback((isOpen: boolean, closeProfile: (() => void) | null) => {
    peopleProfileBackHandlerRef.current = isOpen ? closeProfile : null;
  }, []);

  const handleShellBack = useCallback(() => {
    if (isProjectSettingsView) {
      closeProjectSettings();
      return;
    }

    if (currentView === "people" && peopleProfileBackHandlerRef.current) {
      peopleProfileBackHandlerRef.current();
      return;
    }

    if (currentView === "project") {
      void handleGoToDashboard();
      return;
    }

    if (previousView && previousView !== currentView && previousView !== "welcome") {
      handleNavigate(previousView);
      return;
    }

    handleNavigate("dashboard");
  }, [closeProjectSettings, currentView, handleGoToDashboard, previousView]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return <AccessDenied />;
  }

  if (!user) {
    return (
      <>
        <Splash
          onStartSubscription={handleStartSubscription}
          onExistingUserLogin={handleStartSubscription}
          onBookDemo={handleBookDemo}
          isSigningIn={isStartingSubscription}
        />
        <Toaster position="top-right" richColors closeButton duration={120000} />
      </>
    );
  }

  return (
    <div className={cn("h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-sans flex overflow-hidden transition-colors duration-300", darkMode && "dark")}>
      {user && (
        <Sidebar 
          currentView={currentView} 
          onNavigate={handleNavigate} 
          user={user}
          profile={effectiveProfile}
          darkMode={darkMode}
          toggleDarkMode={toggleDarkMode}
          isFirebaseConnected={isFirebaseConnected}
          onLogout={logout}
        />
      )}
      
      <div className="flex-1 flex flex-col min-w-0 relative">
        <div className="absolute top-0 left-0 right-0 h-[76px] bg-slate-50 dark:bg-slate-950 z-10 pointer-events-none">
          {showShellBackButton ? (
            <div className="absolute inset-x-0 top-5 px-6 sm:px-9 lg:px-12">
              <div className="pointer-events-auto">
                <button
                  type="button"
                  onClick={handleShellBack}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400 dark:hover:border-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                  aria-label="Go back"
                  title="Back"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
              </div>
            </div>
          ) : null}
          <div className="absolute top-6 right-[68px]">
            <img 
              src={darkMode ? "/logo_rev.png" : "/logo.png"} 
              alt="Vine FX" 
              className="h-10 w-auto object-contain"
              referrerPolicy="no-referrer"
            />
          </div>
        </div>
        <main className={cn(
          "w-full px-6 sm:px-9 lg:px-12 pb-8 pt-[88px] flex flex-col flex-1 min-h-0",
          currentView === 'project' ? "overflow-hidden" : "overflow-auto"
        )}>
          {quotaError && (
            <div className="bg-red-50 border-b border-red-100 px-4 py-2 flex items-center justify-center gap-3 text-red-800 text-sm font-medium mb-4 rounded-lg">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              Daily database quota exceeded. Please try again tomorrow.
              <button 
                onClick={() => setQuotaError(false)}
                className="ml-4 text-red-500 hover:text-red-700"
              >
                Dismiss
              </button>
            </div>
          )}
          {storageError && (
            <div className="bg-amber-50 border-b border-amber-100 px-4 py-2 flex items-center justify-center gap-3 text-amber-800 text-sm font-medium mb-4 rounded-lg">
              <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              Local storage is full. Please log in to save your work to the cloud to avoid data loss.
              <button 
                onClick={() => setStorageError(false)}
                className="ml-4 text-amber-500 hover:text-amber-700"
              >
                Dismiss
              </button>
            </div>
          )}
        {isProjectSettingsView ? (
          canManageProjectSettings ? (
            <ProjectSettings
              onBack={() => closeProjectSettings()}
              projectId={projectId}
              setup={setup}
              rates={rates}
              assetRates={assetRates}
              onRatesChange={(r) => { setRates(r); setActiveVersionId(null); }}
              onAssetRatesChange={(r) => { setAssetRates(r); setActiveVersionId(null); }}
              onSetupChange={(s) => { handleSetupChange(s); setActiveVersionId(null); }}
              shots={shots}
              onShotsChange={handleShotsChange}
              globalSettings={globalSettings}
              readOnly={!canManageProjectSettings}
              driveConnected={driveConnected}
              setDriveConnected={setDriveConnected}
              isSettingUpDrive={isSettingUpDrive}
              onSetupDrive={handleSetupDrive}
              onLinkDrive={handleLinkDrive}
              fptLinked={Boolean(projectFptMetadataRef.current.fptLinked || fptProjectId)}
              fptProjectId={fptProjectId}
              fptProjectName={projectFptMetadataRef.current.fptProjectName}
              lastPulledAt={projectFptMetadataRef.current.lastPulledAt}
              onFptLinkStateChange={handleFptLinkStateChange}
              onDeleteProject={() => setIsDeleteProjectConfirmOpen(true)}
            />
          ) : (
            <AccessDenied />
          )
        ) : currentView === "settings" ? (
          <Settings 
            onBack={handleGoToDashboard} 
            projectId={projectId}
            driveConnected={driveConnected}
            setDriveConnected={setDriveConnected}
            settings={globalSettings}
            onSettingsChange={setGlobalSettings}
            isSaving={isSavingSettings}
            onOpenPeoplePerson={(personId) => {
              setPeopleInitialSelectedPersonId(personId);
              handleNavigate("people");
            }}
          />
        ) : currentView === "crm" ? (
          <CRM onOpenProject={handleOpenProjectById} onBack={handleGoToDashboard} />
        ) : currentView === "finance" ? (
          canViewFinance ? <FinanceDashboard onOpenProject={handleOpenProjectById} /> : <AccessDenied />
        ) : currentView === "gantt" ? (
          <ProjectGantt />
        ) : currentView === "people" ? (
          canViewPeopleHr ? (
            <People
              onOpenSettings={() => handleNavigate("settings")}
              onProfileOpenChange={handlePeopleProfileOpenChange}
              initialSelectedPersonId={peopleInitialSelectedPersonId}
              onInitialSelectedPersonConsumed={() => setPeopleInitialSelectedPersonId(null)}
            />
          ) : <AccessDenied />
        ) : currentView === "wiki" ? (
          canViewWiki ? <Wiki /> : <AccessDenied />
        ) : currentView === "calendar" ? (
          hasPermission("calendar.view") ? (
            <ArtistBlockingCalendar canManage={hasPermission("calendar.manage")} />
          ) : (
            <AccessDenied />
          )
        ) : currentView === "onset" ? (
          <div className="flex-1 rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Onset</h2>
            <p className="mt-2 text-slate-500 dark:text-slate-400">This section is currently empty.</p>
          </div>
        ) : currentView === "welcome" ? (
          <InternalWelcome
            displayName={user?.displayName || effectiveProfile?.displayName || user?.email || "Team Member"}
            canViewPeopleHr={canViewPeopleHr}
            onNavigate={(view) => handleNavigate(view)}
            onNewProject={async () => {
              const newProjectId = uuidv4();
              const newProjectSetup = buildDefaultProjectSetup(globalSettings?.defaultAssumptions, newProjectId);
              isNewProjectRef.current = true;
              setProjectId(newProjectId);
              projectFptMetadataRef.current = {};
              handleSetupChange(newProjectSetup);
              
              const defaultRates = globalSettings?.defaultRates || DEFAULT_RATES;
              const defaultAssetRates = globalSettings?.defaultAssetRates || DEFAULT_ASSET_RATES;

              setRates(defaultRates);
              setAssetRates(defaultAssetRates);
              setShots([]);
              setAssets([]);
              setDocuments([]);
              setScenes([]);
              setSequences([]);
              setShootingSchedules([]);
              setPurchaseOrders([]);
              setVersions([]);
              lastSavedDataRef.current = JSON.stringify({ 
                setup: newProjectSetup,
                rates: defaultRates, 
                assetRates: defaultAssetRates, 
                shots: [], 
                assets: [], 
                documents: [],
                scenes: [],
                sequences: [],
                shootingSchedules: [],
                purchaseOrders: []
              });
              handleNavigate("project");
              handleProjectTabChange("show-dashboard", "replace");
              setActiveVersionId(null);
              setTimeout(() => { isNewProjectRef.current = false; }, 500);
            }}
          />
        ) : currentView === "dashboard" ? (
          <ProjectDashboard
            onSelectProject={handleOpenProjectById}
            onNewProject={async () => {
              const newProjectId = uuidv4();
              const newProjectSetup = buildDefaultProjectSetup(globalSettings?.defaultAssumptions, newProjectId);
              isNewProjectRef.current = true;
              setProjectId(newProjectId);
              projectFptMetadataRef.current = {};
              handleSetupChange(newProjectSetup);
              
              const defaultRates = globalSettings?.defaultRates || DEFAULT_RATES;
              const defaultAssetRates = globalSettings?.defaultAssetRates || DEFAULT_ASSET_RATES;

              setRates(defaultRates);
              setAssetRates(defaultAssetRates);
              setShots([]);
              setAssets([]);
              setDocuments([]);
              setScenes([]);
              setSequences([]);
              setShootingSchedules([]);
              setPurchaseOrders([]);
              setVersions([]);
              lastSavedDataRef.current = JSON.stringify({ 
                setup: newProjectSetup,
                rates: defaultRates, 
                assetRates: defaultAssetRates, 
                shots: [], 
                assets: [], 
                documents: [],
                scenes: [],
                sequences: [],
                shootingSchedules: [],
                purchaseOrders: []
              });
              handleNavigate("project");
              handleProjectTabChange("show-dashboard", "replace");
              setActiveVersionId(null);
              setTimeout(() => { isNewProjectRef.current = false; }, 500);
            }}
          />
        ) : (
          <>
            <div className="mb-6 bg-white dark:bg-slate-900 px-4 py-4 rounded-lg border border-slate-200 dark:border-slate-800 flex items-center justify-between shadow-sm shrink-0">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-4">
                  <div className={cn(
                    "rounded-md shadow-sm shrink-0 flex items-center justify-center overflow-hidden",
                    "w-[60px] h-[60px]"
                  )}
                    style={!setup.thumbnail ? { backgroundColor: currentProjectAccentColor, color: currentProjectAccentTextColor } : undefined}
                  >
                    {setup.thumbnail ? (
                      <img 
                        src={setup.thumbnail} 
                        alt={setup.projectName} 
                        className="w-full h-full object-cover"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <Tv className="w-8 h-8" />
                    )}
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="flex flex-col justify-center min-w-0">
                      <h2 className="text-xl font-brandon font-medium text-slate-900 dark:text-slate-100 leading-tight truncate">
                        {setup.projectName || "Untitled Project"}
                      </h2>
                      <div className="flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400 truncate mt-1">
                        <div className="flex items-center gap-2">
                          {productionCompany && getCompanyLogoUrl(productionCompany) && (
                            <img 
                              src={getCompanyLogoUrl(productionCompany)!} 
                              alt={productionCompany.name} 
                              className="w-4 h-4 object-contain"
                            />
                          )}
                          <span>{setup.clientName || "No Production Company"}</span>
                        </div>
                        
                        <div className="flex items-baseline gap-2 pl-4 border-l border-slate-200 dark:border-slate-700 shrink-0">
                          <span className="text-[10px] font-bold uppercase tracking-wider">Total Bid Estimate</span>
                          <span className="font-bold text-indigo-600">
                            {formatCurrency(totalProjectCost, setup.currency)}
                          </span>
                        </div>

                        {user && canEdit && (
                          <div className="flex items-center pl-4 border-l border-slate-200 dark:border-slate-700 shrink-0">
                            {isSaving ? (
                              <span className="flex items-center gap-1 text-[10px] text-indigo-500 font-medium whitespace-nowrap">
                                <div className="w-2 h-2 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                                Saving...
                              </span>
                            ) : lastSaved && (
                              <span className="flex items-center gap-1 text-[10px] text-slate-400 font-medium whitespace-nowrap">
                                <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                                Saved {lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {user && profile && projectId && (
                  <div className="pl-6 border-l border-slate-200 dark:border-slate-700">
                    <PresenceAvatars projectId={projectId} user={user} />
                  </div>
                )}
              </div>

              <div className="flex items-center gap-4">
                {canManageProjectSettings && (
                <motion.button
                  onMouseEnter={handleSettingsMouseEnter}
                  onMouseLeave={handleSettingsMouseLeave}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => openProjectSettings()}
                  className="w-7 h-7 rounded flex items-center justify-center transition-all duration-300 shadow-sm pointer-events-auto"
                  style={{ backgroundColor: "#c4beb5" }}
                  title="Project Settings"
                  >
                    <motion.div
                      animate={settingsControls}
                    initial={{ scale: 1, rotate: 0 }}
                  >
                    <SettingsIcon className="w-[18px] h-[18px] text-white" />
                    </motion.div>
                </motion.button>
                )}

                <button
                  onClick={() => setIsExportModalOpen(true)}
                  disabled={isSaving || isProjectLoading}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors shadow-sm",
                    isSaving 
                      ? "bg-slate-100 text-slate-400 cursor-not-allowed" 
                      : "bg-[#ffcc01] text-slate-900 hover:bg-[#e6b801]"
                  )}
                >
                  {isSaving ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  {isSaving ? "Saving..." : "Export Quote"}
                </button>
              </div>
            </div>
            <div className="mb-4 shrink-0 overflow-x-auto border-b border-slate-200 dark:border-slate-800">
              <nav className="-mb-px flex min-w-max space-x-8 pl-4 pr-4 sm:pl-6" aria-label="Tabs">
                {[
                  { id: 'show-dashboard', label: '', ariaLabel: 'Project home', icon: Home },
                  { id: 'projectdetails', label: 'Project Details', icon: Settings2 },
                  { id: 'shots', label: 'Shot Bidding', icon: Film },
                  { id: 'assets', label: 'Asset Bidding', icon: Database },
                  { id: 'documents', label: 'Documents', icon: FileText },
                  { id: 'exports', label: 'Bid Summary', icon: PieChart },
                  { id: 'shotgrid', label: 'Shotgrid', icon: LayoutGrid },
                  ...(canViewFinance ? [{ id: 'finance', label: 'Finance', icon: WalletCards }] : []),
                  ...(canViewFinance ? [{ id: 'utilisation', label: 'Utilisation', icon: Layers3 }] : []),
                  { id: 'calendar', label: 'Calendar', icon: Calendar },
                  { id: 'videoanalysis', label: 'Video Analysis', icon: Scan },
                  { id: 'meetings', label: 'Meetings', icon: Calendar }
                ].map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      aria-label={tab.ariaLabel || tab.label}
                      onClick={() => handleProjectTabChange(tab.id as ProjectTab)}
                      className={`
                        whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors flex items-center ${tab.label ? "gap-[3px]" : "justify-center"} rounded-none bg-transparent outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0
                        ${activeTab === tab.id
                          ? tab.id === 'show-dashboard'
                            ? 'border-[#ffcc01] text-[#3397da] dark:text-[#3397da]'
                            : 'border-[#ffcc01] text-[#ffcc01] dark:text-[#ffcc01]'
                          : tab.id === 'show-dashboard'
                            ? 'border-transparent text-[#3397da] hover:text-[#2f86c4] hover:border-slate-300 dark:text-[#3397da] dark:hover:text-[#5aaee8] dark:hover:border-slate-700'
                            : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300 dark:text-slate-500 dark:hover:text-slate-300 dark:hover:border-slate-700'}
                      `}
                    >
                      <Icon className="w-[14px] h-[14px]" />
                      {tab.label ? tab.label : null}
                    </button>
                  );
                })}
              </nav>
            </div>

            <div className={cn(
              "flex-1 min-h-0",
              ["shots", "assets"].includes(activeTab) ? "flex flex-col" : "overflow-auto"
            )}>
              {activeTab === 'projectdetails' && (
                <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
                  <div className="xl:col-span-3 space-y-2">
                    <ProjectSetupForm
                      setup={setup}
                      onChange={(s) => { handleSetupChange(s); setActiveVersionId(null); }}
                      readOnly={!canEdit}
                      defaultAssumptions={globalSettings?.defaultAssumptions}
                    />
                    <ProjectInsights
                      projectId={projectId}
                      projectName={setup.projectName}
                      clientName={setup.clientName}
                      userUid={user?.uid ?? null}
                    />
                  </div>
                  <div className="xl:col-span-1 mt-0">
                    <VersionHistory 
                      versions={versions} 
                      onRevert={handleRevertVersion} 
                      onDelete={handleDeleteVersion}
                      readOnly={!canEdit} 
                      activeVersionId={activeVersionId}
                      currentWorkingVersionCode={exportVersionCode}
                      currentWorkingTotalCost={totalProjectCost}
                    />
                  </div>
                </div>
              )}

              {activeTab === 'show-dashboard' && (
                <ProjectShowDashboard
                  projectId={projectId}
                  projectName={setup.projectName}
                  fallbackImageUrl={setup.thumbnail}
                  fallbackAccentColor={setup.themeAccentColor}
                />
              )}

              {activeTab === 'shots' && (
                <div className="flex-1 min-h-0">
                  <ShotTable
                    shots={shots}
                    rates={rates}
                    setup={setup}
                    importRevision={shotImportRevision}
                    onChange={handleShotsChange}
                    onAddShot={handleAddShot}
                    onDuplicateShot={handleDuplicateShot}
                    onDeleteShot={handleDeleteShot}
                    onUpdateSetup={(updates) => { handleSetupChange(prev => ({ ...prev, ...updates })); setActiveVersionId(null); }}
                    readOnly={!canEdit}
                    documents={documents}
                    scenes={scenes}
                    onFilterStateChange={setShotExportFilter}
                  />
                </div>
              )}

              {activeTab === 'assets' && (
                <div className="flex-1 min-h-0">
                  <AssetTable
                    assets={assets}
                    rates={assetRates}
                    setup={setup}
                    onChange={handleAssetsChange}
                    onAddAsset={() => { handleAddAsset(); setActiveVersionId(null); }}
                    onDuplicateAsset={(id) => { handleDuplicateAsset(id); setActiveVersionId(null); }}
                    onDeleteAsset={(id) => { handleDeleteAsset(id); setActiveVersionId(null); }}
                    readOnly={!canEdit}
                    onFilterStateChange={setAssetExportFilter}
                  />
                </div>
              )}

              {activeTab === 'shotgrid' && (
                <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
                  <div className="xl:col-span-4">
                    <div className="bg-white dark:bg-slate-900 p-4 rounded-lg border border-slate-200 dark:border-slate-800 flex items-center justify-between mb-6 shadow-sm shrink-0">
                      <SectionPillHeader
                        icon={LayoutGrid}
                        title="Shotgrid"
                        description="Review Flow Production Tracking structure, sync state, and project-side status signals."
                      />
                    </div>
                    <ShotGridDashboard
                      projectId={projectId}
                      setup={setup}
                      rates={rates}
                      assetRates={assetRates}
                      shots={shots}
                      assets={assets}
                      fptProjectId={fptProjectId}
                      lastPulledAt={projectFptMetadataRef.current.lastPulledAt}
                      financials={projectFptMetadataRef.current.financials}
                      profitability={projectFptMetadataRef.current.profitability}
                      fptStructureInspection={projectFptMetadataRef.current.fptStructureInspection as any}
                      onProjectMetadataRefresh={handleShotGridMetadataRefresh}
                    />
                  </div>
                </div>
              )}

              {activeTab === 'finance' && (
                <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
                  <div className="xl:col-span-4">
                    {canViewFinance ? (
                      <>
                        <div className="bg-white dark:bg-slate-900 p-4 rounded-lg border border-slate-200 dark:border-slate-800 flex items-center justify-between mb-6 shadow-sm shrink-0">
                          <SectionPillHeader
                            icon={WalletCards}
                            title="Finance"
                            description="Track project-side labour, reconciliation, and forecast alignment without leaving the show."
                          />
                        </div>
                        <FinanceDashboard
                          onOpenProject={handleOpenProjectById}
                          projectId={projectId}
                          embedded
                        />
                      </>
                    ) : (
                      <AccessDenied />
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'utilisation' && (
                <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
                  <div className="xl:col-span-4">
                    {canViewFinance ? (
                      <ProjectUtilisation
                        projectId={projectId}
                        fptProjectId={fptProjectId}
                        projectName={setup.projectName}
                        projectStartDate={setup.projectStartEst}
                        projectEndDate={setup.projectEndEst}
                        shots={shots}
                        assets={assets}
                        rates={rates}
                        assetRates={assetRates}
                        supervisionPercentage={setup.supervisionPercentage}
                        currency={setup.currency}
                      />
                    ) : (
                      <AccessDenied />
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'calendar' && (
                <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
                  <div className="xl:col-span-4">
                    <ProjectShootCalendar
                      projectName={setup.projectName}
                      shootingSchedules={shootingSchedules}
                    />
                  </div>
                </div>
              )}

              {activeTab === 'videoanalysis' && (
                <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
                  <div className="xl:col-span-3">
                    <VideoAnalysis />
                  </div>
                </div>
              )}

              {activeTab === 'meetings' && (
                <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
                  <div className="xl:col-span-3">
                    <Meetings projectId={projectId} projectName={setup.projectName} />
                  </div>
                </div>
              )}

              {projectId && user && (
                <Documents
                  documents={documents}
                  setDocuments={setDocuments}
                  globalSettings={globalSettings}
                  onImportShots={handleImportShots}
                  onImportAssets={handleImportAssets}
                  onImportOnset={handleImportOnset}
                  onImportTopSheet={handleImportTopSheet}
                  onImportShootingSchedule={handleImportShootingSchedule}
                  existingShots={shots}
                  existingAssets={assets}
                  existingScenes={scenes}
                  existingSequences={sequences}
                  onUpdateSetup={handleDocumentsSetupUpdate}
                  onUpdateRates={handleDocumentsRatesUpdate}
                  onUpdateAssetRates={handleDocumentsAssetRatesUpdate}
                  rates={rates}
                  assetRates={assetRates}
                  setup={setup}
                  projectId={projectId}
                  user={user}
                  handleSetupDrive={handleSetupDrive}
                  handleLinkDrive={handleLinkDrive}
                  handleDisconnectDrive={handleDisconnectDrive}
                  isSettingUpDrive={isSettingUpDrive}
                  driveConnected={driveConnected}
                  isAdmin={isAdmin}
                  onSaveDocument={saveDocument}
                  onRemoveDocument={removeDocument}
                  onPersistScriptStructure={persistScriptStructureFromDocument}
                  onImportStateChange={handleQuoteImportStateChange}
                  uiVisible={currentView === "project" && activeTab === "documents"}
                />
              )}

              {activeTab === 'exports' && (
                <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
                  <div className="xl:col-span-4">
                    <div className="bg-white dark:bg-slate-900 p-4 rounded-lg border border-slate-200 dark:border-slate-800 flex items-center justify-between mb-6 shadow-sm shrink-0">
                      <SectionPillHeader
                        icon={FileText}
                        title="Bid Summary"
                        description="Review locked bid history, commercial status, overages, and purchase orders."
                      />
                    </div>
                    <BidSummaryDashboard
                      setup={setup}
                      rates={rates}
                      assetRates={assetRates}
                      shots={shots}
                      assets={assets}
                      versions={versions}
                      purchaseOrders={purchaseOrders}
                      onAddPurchaseOrder={handleAddPurchaseOrder}
                    />
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </main>

      <ExportVersionModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        exportVersionCode={exportVersionCode}
        exportVersionName={exportVersionName}
        hasActiveSubset={isSubsetExportActive(shotExportFilter, assetExportFilter)}
        currentOnsetDays={setup.onsetSupervision || 0}
        onConfirm={handleCreateExportVersion}
      />

      <ConfirmModal
        isOpen={!!versionToRevert}
        onClose={() => setVersionToRevert(null)}
        onConfirm={confirmRevertVersion}
        title="Revert Version"
        message={`Are you sure you want to revert to version "${versionToRevert?.versionName}"? Current unsaved changes will be lost.`}
        confirmText="Revert"
        isDestructive={true}
      />

      <ConfirmModal
        isOpen={!!versionToDelete}
        onClose={() => setVersionToDelete(null)}
        onConfirm={confirmDeleteVersion}
        title="Delete Version"
        message="Are you sure you want to delete this version? This action cannot be undone."
        confirmText="Delete"
        isDestructive={true}
      />
      <ConfirmModal
        isOpen={isDeleteProjectConfirmOpen}
        onClose={() => setIsDeleteProjectConfirmOpen(false)}
        onConfirm={handleDeleteProject}
        title="Delete Project"
        message="Are you sure you want to delete this project? This action cannot be undone and will remove all versions and draft data."
        confirmText="Delete"
        isDestructive={true}
      />
      <AIAssistant 
        activeTab={activeTab} 
        projectName={setup.projectName}
        shotCount={shots.length}
        assetCount={assets.length}
        isOpen={isAIAssistantOpen}
        onClose={() => setIsAIAssistantOpen(false)}
      />
      <Toaster position="top-right" richColors closeButton duration={120000} />
      </div>
    </div>
  );
}
